import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/index.css';

// 👇 IMPORTAÇÃO DOS ALERTAS PADRONIZADOS
import { showSuccess, showError, showWarning } from '../utils/alertas';

import EdicaoModal from '../components/EdicaoModal';
import AdicionarMarcoModal from '../components/AdicionarMarcoModal';
import FluxogramaModal from '../components/FluxogramaModal';
import EditarContratoModal from '../components/EditarContratoModal';
import ExcluirModal from '../components/ExcluirModal';
import PlanejamentoChart from '../components/PlanejamentoChart';

const PencilIcon = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
  </svg>
);

// ⚠️ MANTENHA TRUE PARA PRODUÇÃO
const IS_PROD = true;

const API_BASE_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br/api' : 'http://localhost:3001/api';
const SERVER_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br' : 'http://localhost:3001';

function DetalheEmpreendimento() {
    const { id } = useParams();
    const navigate = useNavigate();

    // 🔐 1. RECUPERAÇÃO DO TOKEN
    const token = localStorage.getItem('token');
    const isAdmin = !!token;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('Visão Geral');

    // Modais de Edição/Criação
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMarcoModalOpen, setIsMarcoModalOpen] = useState(false);
    const [isFluxoModalOpen, setIsFluxoModalOpen] = useState(false);
    const [isContratoModalOpen, setIsContratoModalOpen] = useState(false);

    // 👇 ESTADOS PARA O MODAL DE EXCLUSÃO
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const [selectedContratoId, setSelectedContratoId] = useState(null);
    const [marcoEmEdicao, setMarcoEmEdicao] = useState(null);
    const [contratoEmEdicao, setContratoEmEdicao] = useState(null);

    const [expandedContrato, setExpandedContrato] = useState(null);
    const [filtroContratoId, setFiltroContratoId] = useState('Todos');
    const [fluxoIndex, setFluxoIndex] = useState(0);

    // 🔐 ESTADO PARA O ACCORDION DE PLANEJAMENTO MENSAL (RESTRITO)
    const [isPlanejamentoExpanded, setIsPlanejamentoExpanded] = useState(false);

    // 📝 NOVO ESTADO LOCAL PARA COMPORTAR A EDIÇÃO EM GRADE DAS METAS
    const [metasEditaveis, setMetasEditaveis] = useState([]);
    const [salvandoMetaId, setSalvandoMetaId] = useState(null);

    // 📝 ESTADO PARA NOVAS LINHAS AINDA NÃO SALVAS (PRÉ-POST)
    const [novaLinhasMeta, setNovaLinhasMeta] = useState([]);

    // 📅 COLUNAS DE MESES DINÂMICAS DETECTADAS DO BANCO (2026 e futuros)
    const [colunasMeses, setColunasMeses] = useState(() => {
        const currentYear = new Date().getFullYear();
        const cols = [];
        // Gera todos os meses para o ano atual e os próximos 4 anos por padrão
        for (let y = currentYear; y <= currentYear + 4; y++) {
            for (let m = 1; m <= 12; m++) {
                cols.push(`${y}-${String(m).padStart(2, '0')}`);
            }
        }
        return cols;
    });

    // ➕ FUNÇÃO PARA ADICIONAR NOVO ANO DINAMICAMENTE
    const handleAddNextYear = () => {
        setColunasMeses(prev => {
            let maxYear = new Date().getFullYear();
            prev.forEach(col => {
                const y = parseInt(col.split('-')[0], 10);
                if (y > maxYear) maxYear = y;
            });
            const nextYear = maxYear + 1;
            const newCols = [];
            for (let m = 1; m <= 12; m++) {
                newCols.push(`${nextYear}-${String(m).padStart(2, '0')}`);
            }
            return [...prev, ...newCols];
        });
    };

    // 🔐 ESTADO PARA O ACCORDION DE ANOS MÊS A MÊS
    const [expandedYears, setExpandedYears] = useState(() => {
        const anoAtual = new Date().getFullYear();
        return { [anoAtual]: true }; // Ano atual expandido por padrão
    });

    // 🗑️ ESTADO PARA MODAL DE EXCLUSÃO DE ANO COMPLETO
    const [isDeleteYearModalOpen, setIsDeleteYearModalOpen] = useState(false);
    const [yearToDelete, setYearToDelete] = useState(null);
    const [excluindoAno, setExcluindoAno] = useState(false);

    const handleOpenDeleteYearModal = (ano) => {
        setYearToDelete(ano);
        setIsDeleteYearModalOpen(true);
    };

    const handleConfirmDeleteYear = async () => {
        if (!yearToDelete) return;
        setExcluindoAno(true);
        try {
            const response = await fetch(`${API_BASE_URL}/empreendimentos/${id}/planejamento-ano/${yearToDelete}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                }
            });

            let errData = {};
            try { errData = await response.json(); } catch (e) {}

            if (response.ok) {
                showSuccess("Ano Excluído!", `O ano ${yearToDelete} foi removido do planejamento com sucesso.`);
                // Remove as colunas locais do ano excluído
                setColunasMeses(prev => prev.filter(c => !c.startsWith(`${yearToDelete}-`)));
                fetchDetalhes();
                setIsDeleteYearModalOpen(false);
                setYearToDelete(null);
            } else {
                showError("Erro ao Excluir", errData.message || "Tente novamente.");
            }
        } catch (error) {
            console.error("❌ Erro de conexão na exclusão do ano:", error);
            showError("Erro de Conexão", "Não foi possível se conectar ao servidor.");
        } finally {
            setExcluindoAno(false);
        }
    };

    // 🔄 LÓGICA DE AGRUPAMENTO DE COLUNAS POR ANO
    const colunasAgrupadas = useMemo(() => {
        const grupos = {};
        colunasMeses.forEach(col => {
            let ano;
            if (col.includes('-')) {
                ano = col.split('-')[0];
            } else {
                ano = col.replace(/\D/g, ''); 
                if (ano.length !== 4) ano = col; // Fallback se não encontrar os 4 dígitos
            }
            if (!grupos[ano]) grupos[ano] = [];
            grupos[ano].push(col);
        });
        return grupos;
    }, [colunasMeses]);

    // 📝 ESTADOS PARA O POPUP DE EDIÇÃO RÁPIDA DE CÉLULA
    const [isCellModalOpen, setIsCellModalOpen] = useState(false);
    const [cellEmEdicao, setCellEmEdicao] = useState(null); // { rowId: 1, coluna: 'jan26' }
    const [edicaoValor, setEdicaoValor] = useState('');
    const [edicaoStatus, setEdicaoStatus] = useState('Projeção');

    const filteredMetas = metasEditaveis;

    // Novos estados para edição em massa e por modal
    const [isMonthModalOpen, setIsMonthModalOpen] = useState(false);
    const [selectedMonthCol, setSelectedMonthCol] = useState(null);
    const [monthValues, setMonthValues] = useState({});

    const [isRowModalOpen, setIsRowModalOpen] = useState(false);
    const [selectedMetaRow, setSelectedMetaRow] = useState(null);
    const [rowValues, setRowValues] = useState({});

    const [salvandoModal, setSalvandoModal] = useState(false);
    const [showDetalhesContratos, setShowDetalhesContratos] = useState(false);
    const [dataAtuInput, setDataAtuInput] = useState('');
    const [hoveredMarco, setHoveredMarco] = useState(null);

    // Helper: converte nome da coluna de valor para o nome real da coluna de status no banco
    const getDbStatusKey = (col) => `${col}_status`;

    // Helper: formata o cabeçalho dos períodos dinâmicos
    const formatPeriodHeader = (periodo) => {
        if (!periodo) return '';
        if (periodo.includes('-')) {
            const [ano, mes] = periodo.split('-');
            const meses = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
            return `${meses[parseInt(mes) - 1]}/${ano.slice(-2)}`;
        }
        return `ANO/${periodo.slice(-2)}`;
    };

    // Helper: interpreta números
    // Aceita tanto formato BR (1.234,56) quanto US (1234.56)
    // Se o usuário digitou '7.102' ele deve manter 7.102, e não 7102.
    // Assim como 18.146.
    const parseNumberStrict = (v) => {
        if (v === '' || v === null || v === undefined) return 0;
        const str = String(v).trim();
        // Se tem vírgula, assume que é BR decimal (123,45 -> 123.45)
        // Se só tem ponto e é apenas um, assume que é decimal (7.102 -> 7.102)
        const clean = str.replace(',', '.');
        const n = parseFloat(clean);
        return isNaN(n) ? 0 : n;
    };

    const handleMarcoMouseEnter = (e, marco) => {
        if (!marco.descricao) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setHoveredMarco({
            titulo: marco.titulo,
            descricao: marco.descricao,
            x: rect.left + window.scrollX + (rect.width / 2),
            y: rect.top + window.scrollY
        });
    };

    const handleMarcoMouseLeave = () => {
        setHoveredMarco(null);
    };


    const somasCalculadas = useMemo(() => {
        let consolidado = 0;
        let projecao = 0;
        let consolidadoAnoAtual = 0;

        const currentYearStr = String(new Date().getFullYear()); // "2026"

        if (metasEditaveis && metasEditaveis.length > 0) {
            metasEditaveis.forEach(row => {
                colunasMeses.forEach(col => {
                    const val = Number(row[col] || 0);
                    const statusKey = getDbStatusKey(col);
                    const status = row[statusKey] || 'Projeção';

                    if (status === 'Consolidado') {
                        consolidado += val;
                    } else {
                        projecao += val;
                    }
                    if (col.startsWith(currentYearStr)) {
                        consolidadoAnoAtual += val;
                    }
                });
            });
        }

        return { consolidado, projecao, consolidadoAnoAtual };
    }, [metasEditaveis, colunasMeses]);

    const somaMensal = useMemo(() => {
        const somas = {};
        colunasMeses.forEach(col => {
            let total = 0;
            if (metasEditaveis && metasEditaveis.length > 0) {
                metasEditaveis.forEach(row => {
                    total += Number(row[col] || 0);
                });
            }
            somas[col] = total;
        });

        Object.keys(colunasAgrupadas).forEach(ano => {
            const col = `ano${ano}`;
            let total = 0;
            if (metasEditaveis && metasEditaveis.length > 0) {
                metasEditaveis.forEach(row => {
                    total += Number(row[col] || 0);
                });
            }
            somas[col] = total;
        });

        return somas;
    }, [metasEditaveis, colunasMeses, colunasAgrupadas]);


    const fetchDetalhes = useCallback(async () => {
        try {
            const headers = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Log carga de metas
            console.log('🔍 [FRONT] Metas carregadas para a ETE:', id);
            let response = await fetch(`${API_BASE_URL}/empreendimentos/${id}`, {
                method: 'GET',
                headers: headers
            });

            if (response.status === 403 && token) {
                console.warn("⚠️ Token expirado ou inválido. Removendo do localStorage e acessando de forma pública.");
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.reload();
                return;
            }

            if (response.ok) {
                const jsonResult = await response.json();
                setData(jsonResult);
                setDataAtuInput(jsonResult.planejamento_ultima_atualizacao || '');

                // Popula o estado editável com o que veio do banco
                if (jsonResult.planejamento && jsonResult.planejamento.dados_brutos) {
                    const dados = jsonResult.planejamento.dados_brutos;
                    setMetasEditaveis(dados);
                    
                    // Expande o estado local de colunasMeses caso os dados do backend tenham anos além do padrão
                    let maxYearInDb = new Date().getFullYear() + 4;
                    dados.forEach(row => {
                        Object.keys(row).forEach(key => {
                            if (key.match(/^\d{4}-\d{2}$/)) {
                                const year = parseInt(key.split('-')[0], 10);
                                if (year > maxYearInDb) maxYearInDb = year;
                            } else if (key.match(/^ano\d{4}$/)) {
                                const year = parseInt(key.replace('ano', ''), 10);
                                if (year > maxYearInDb) maxYearInDb = year;
                            }
                        });
                    });

                    setColunasMeses(prev => {
                        let currentMax = new Date().getFullYear();
                        prev.forEach(col => {
                            const y = parseInt(col.split('-')[0], 10);
                            if (y > currentMax) currentMax = y;
                        });
                        
                        if (maxYearInDb > currentMax) {
                            const newCols = [...prev];
                            for (let y = currentMax + 1; y <= maxYearInDb; y++) {
                                for (let m = 1; m <= 12; m++) {
                                    newCols.push(`${y}-${String(m).padStart(2, '0')}`);
                                }
                            }
                            return newCols;
                        }
                        return prev;
                    });
                }

                // 📊 REGISTRO SILENCIOSO DE TELEMETRIA
                try {
                    fetch(`${API_BASE_URL}/telemetria/log`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                        },
                        body: JSON.stringify({
                            empreendimento_id: id,
                            aba_acessada: activeTab === 'Visão Geral' ? 'visao_geral' : 'contratos'
                        })
                    }).catch(() => {});
                } catch (e) {}

            } else {
                navigate('/404');
            }
        } catch (error) {
            console.error("❌ [FRONTEND ERROR - fetchDetalhes]:", error);
            showError("Erro de Conexão", "Não foi possível carregar os dados.");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, navigate, token]);

    useEffect(() => { fetchDetalhes(); }, [fetchDetalhes]);

    // 💾 NOVO HANDLER PARA ALTERAR DIRETAMENTE A CÉLULA NO ESTADO LOCAL
    const handleInputChange = (metaId, campo, valor) => {
        setMetasEditaveis(prev =>
            prev.map(item => {
                if (item.id === metaId) {
                    return { ...item, [campo]: valor };
                }
                return item;
            })
        );
    };

    // 💾 NOVO HANDLER PARA ABRIR O MODAL DE EDIÇÃO DE CÉLULA
    const handleOpenCellEdit = (rowId, coluna, valorAtual, statusAtual) => {
        if (!isAdmin) {
            console.warn("⚠️ [ACESSO NEGADO]: Tentativa de edição de célula por usuário sem permissão.");
            showWarning("Acesso Restrito", "Você não tem permissão para realizar esta alteração.");
            return;
        }
        setCellEmEdicao({ rowId, coluna });
        setEdicaoValor(valorAtual === 0 ? '' : valorAtual);
        setEdicaoStatus(statusAtual || 'Projeção');
        setIsCellModalOpen(true);
    };

    // 💾 NOVO HANDLER PARA SALVAR A CÉLULA NO MODAL RÁPIDO
    const handleSaveCell = async () => {
        if (!cellEmEdicao) return;
        const { rowId, coluna } = cellEmEdicao;

        setMetasEditaveis(prev =>
            prev.map(item => {
                if (item.id === rowId) {
                    const valorNum = parseNumberStrict(edicaoValor);
                    const statusDbKey = getDbStatusKey(coluna);
                    return {
                        ...item,
                        [coluna]: valorNum,
                        [statusDbKey]: edicaoStatus
                    };
                }
                return item;
            })
        );

        setIsCellModalOpen(false);

        try {
            const valorNum = parseNumberStrict(edicaoValor);
            console.log('✈️ [FRONT] Enviando payload PUT:', { coluna, valor: valorNum, status: edicaoStatus });
            const response = await fetch(`${API_BASE_URL}/metas-planejamento/${rowId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    coluna: coluna,
                    valor: valorNum,
                    status: edicaoStatus
                })
            });

            if (response.ok) {
                showSuccess("Salvo!", "Meta atualizada com sucesso.");
                fetchDetalhes();
            } else {
                const err = await response.json();
                showError("Erro ao Salvar", err.message || "Tente novamente.");
            }
        } catch (error) {
            showError("Erro de Rede", "Não foi possível conectar ao servidor.");
        }
    };

    const handleSalvarDataPlanejamento = async (novaData) => {
        try {
            console.log('✈️ [FRONT] Atualizando data do planejamento:', novaData);
            const response = await fetch(`${API_BASE_URL}/empreendimentos/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    planejamento_ultima_atualizacao: novaData
                })
            });

            if (response.ok) {
                showSuccess("Salvo!", "Data de atualização salva.");
                setData(prev => ({ ...prev, planejamento_ultima_atualizacao: novaData }));
            } else {
                const err = await response.json();
                showError("Erro ao Salvar", err.message || "Tente novamente.");
            }
        } catch (error) {
            showError("Erro de Rede", "Não foi possível conectar ao servidor.");
        }
    };

    // Abre o modal de edição de todos os contratos para um mês específico
    const handleOpenMonthEdit = (mesChave) => {
        setSelectedMonthCol(mesChave);
        const initialValues = {};
        metasEditaveis.forEach(meta => {
            const statusKey = getDbStatusKey(mesChave);
            initialValues[meta.id] = {
                val: meta[mesChave] === 0 ? '' : meta[mesChave],
                status: meta[statusKey] || 'Projeção'
            };
        });
        setMonthValues(initialValues);
        setIsMonthModalOpen(true);
    };

    // Salva a edição em massa do mês
    const handleSaveMonth = async () => {
        if (!selectedMonthCol) return;
        setSalvandoModal(true);
        try {
            const updates = Object.keys(monthValues).map(async (metaId) => {
                const { val, status } = monthValues[metaId];
                const valorNum = parseNumberStrict(val);

                // Atualiza localmente
                setMetasEditaveis(prev =>
                    prev.map(item => {
                        if (String(item.id) === String(metaId)) {
                            const statusDbKey = getDbStatusKey(selectedMonthCol);
                            return {
                                ...item,
                                [selectedMonthCol]: valorNum,
                                [statusDbKey]: status
                            };
                        }
                        return item;
                    })
                );

                return fetch(`${API_BASE_URL}/metas-planejamento/${metaId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        coluna: selectedMonthCol,
                        valor: valorNum,
                        status: status
                    })
                });
            });

            await Promise.all(updates);
            showSuccess("Salvo!", "Todos os valores do mês foram atualizados.");
            fetchDetalhes();
            setIsMonthModalOpen(false);
        } catch (error) {
            showError("Erro ao Salvar", "Ocorreu um erro ao atualizar os contratos.");
        } finally {
            setSalvandoModal(false);
        }
    };

    // Abre o modal de edição de toda a linha do contrato (todos os meses)
    const handleOpenRowEdit = (meta) => {
        setSelectedMetaRow(meta);
        const initialValues = {};
        colunasMeses.forEach(col => {
            const statusKey = getDbStatusKey(col);
            initialValues[col] = {
                val: meta[col] === 0 ? '' : meta[col],
                status: meta[statusKey] || 'Projeção'
            };
        });
        setRowValues(initialValues);
        setIsRowModalOpen(true);
    };

    // Salva a edição de toda a linha do contrato
    const handleSaveRow = async () => {
        if (!selectedMetaRow) return;
        setSalvandoModal(true);
        try {
            const payload = {
                contrato_id: selectedMetaRow.contrato_id,
                objeto_reduzido: selectedMetaRow.objeto_reduzido
            };
            colunasMeses.forEach(col => {
                const { val, status } = rowValues[col];
                const valorNum = parseNumberStrict(val);
                const statusDbKey = getDbStatusKey(col);
                payload[col] = valorNum;
                payload[statusDbKey] = status;
            });

            // Atualiza estado local
            setMetasEditaveis(prev =>
                prev.map(item => {
                    if (item.id === selectedMetaRow.id) {
                        return {
                            ...item,
                            ...payload
                        };
                    }
                    return item;
                })
            );

            const response = await fetch(`${API_BASE_URL}/metas-planejamento/${selectedMetaRow.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                showSuccess("Salvo!", "Linha do contrato atualizada com sucesso.");
                fetchDetalhes();
                setIsRowModalOpen(false);
            } else {
                const err = await response.json();
                showError("Erro ao Salvar", err.message || "Tente novamente.");
            }
        } catch (error) {
            showError("Erro de Rede", "Não foi possível conectar ao servidor.");
        } finally {
            setSalvandoModal(false);
        }
    };

    // ➕ ADICIONA UMA NOVA LINHA VAZIA AO ESTADO LOCAL (ANTES DO POST)
    const handleAdicionarNovaLinha = () => {
        const novaLinha = {
            _tempId: Date.now(), // ID temporário para controle do estado local
            contrato_id: '',
            objeto_reduzido: '',
        };
        // Inicializa todos os meses da coluna como 0 e status Projeção
        colunasMeses.forEach(col => {
            novaLinha[col] = 0;
            novaLinha[`${col}_status`] = 'Projeção';
        });
        setNovaLinhasMeta(prev => [...prev, novaLinha]);
    };

    // 💾 HANDLER PARA ATUALIZAR CAMPO DE NOVA LINHA NO ESTADO LOCAL
    const handleInputNovaLinha = (tempId, campo, valor) => {
        setNovaLinhasMeta(prev =>
            prev.map(item => item._tempId === tempId ? { ...item, [campo]: valor } : item)
        );
    };

    // 💾 HANDLER PARA SALVAR NOVA LINHA VIA POST /api/metas-planejamento
    const handleSalvarNovaLinha = async (novaLinha) => {
        const contratoId = (novaLinha.contrato_id || '').trim();
        const objetoReduzido = (novaLinha.objeto_reduzido || '').trim();

        if (!contratoId || !objetoReduzido) {
            showError("Campos Obrigatórios", "O ID do Contrato e o Objeto Reduzido são obrigatórios.");
            return;
        }

        setSalvandoMetaId(novaLinha._tempId);
        try {
            // Prepare payload with all month fields and statuses
            const payload = {
                empreendimento_id: id,
                contrato_id: contratoId,
                contrato: contratoId, // fallback para compatibilidade
                objeto_reduzido: objetoReduzido,
            };
            // Append dynamic month values and status
            colunasMeses.forEach(col => {
                payload[col] = novaLinha[col] === '' ? 0 : novaLinha[col];
                payload[`${col}_status`] = novaLinha[`${col}_status`] || 'Projeção';
            });
            // Log before sending
            console.log('✈️ [FRONT] Enviando payload para o servidor:', payload);
            const response = await fetch(`${API_BASE_URL}/metas-planejamento`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                showSuccess('Criado!', 'Novo contrato de planejamento adicionado com sucesso.');
                // Remove temporary line and refresh data
                setNovaLinhasMeta(prev => prev.filter(l => l._tempId !== novaLinha._tempId));
                fetchDetalhes();
            } else {
                const err = await response.json();
                showError('Erro ao Criar', err.message || 'Tente novamente.');
            }
        } catch (error) {
            showError('Erro de Rede', 'Não foi possível conectar ao servidor.');
        } finally {
            setSalvandoMetaId(null);
        }
    };

    // 💾 NOVO HANDLER PARA EXECUTAR A REQUISIÇÃO PUT DE SALVAMENTO MANUAL DA LINHA
    const handleSalvarMetaManual = async (metaRow) => {
        setSalvandoMetaId(metaRow.id);
        try {
            const response = await fetch(`${API_BASE_URL}/metas-planejamento/${metaRow.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    contrato_id: metaRow.contrato_id,
                    objeto_reduzido: metaRow.objeto_reduzido
                })
            });

            if (response.ok) {
                showSuccess("Salvo!", "Linha de planejamento atualizada com sucesso.");
                fetchDetalhes(); // Recarrega os totais e a visão consolidada vinda do server
            } else {
                const err = await response.json();
                showError("Erro ao Salvar", err.message || "Tente novamente.");
            }
        } catch (error) {
            showError("Erro de Rede", "Não foi possível conectar ao servidor.");
        } finally {
            setSalvandoMetaId(null);
        }
    };

    // 🗑️ HANDLER PARA AÇÃO DE EXCLUSÃO DO CONTRATO DE PLANEJAMENTO
    const handleDeletePlanejamento = (metaRow) => {
        setItemToDelete({
            tipo: 'contrato_planejamento',
            id: metaRow.id,
            label: `Contrato de Planejamento "${metaRow.contrato_id || '-'}"`
        });
        setIsDeleteModalOpen(true);
    };

    // ✅ Handlers Originais Preservados
    const openMarcoModal = (cId) => { setSelectedContratoId(cId); setMarcoEmEdicao(null); setIsMarcoModalOpen(true); };
    const handleCloseMarcoModal = () => { setIsMarcoModalOpen(false); setMarcoEmEdicao(null); };
    const handleEditarMarco = (m, cId) => {
        if (!isAdmin) {
            console.warn("⚠️ [ACESSO NEGADO]: Tentativa de edição de marco por usuário sem permissão.");
            showWarning("Acesso Restrito", "Você não tem permissão para realizar esta alteração.");
            return;
        }
        setSelectedContratoId(cId);
        setMarcoEmEdicao(m);
        setIsMarcoModalOpen(true);
    };

    const handleEditarContrato = (contrato) => {
        setContratoEmEdicao(contrato);
        setIsContratoModalOpen(true);
    };

    const handleDeleteArquivo = (label) => {
        const tipo = label === 'Fluxograma' ? 'imagem' : 'video';
        setItemToDelete({ tipo, label });
        setIsDeleteModalOpen(true);
    };

    const confirmarExclusaoReal = async () => {
        if (!itemToDelete) return;

        const currentItem = itemToDelete;

        // Close confirmation modal immediately to avoid layering/z-index issues with alerts
        setIsDeleteModalOpen(false);
        setItemToDelete(null);

        try {
            if (currentItem.tipo === 'contrato_planejamento') {
                const response = await fetch(`${API_BASE_URL}/metas-planejamento/${currentItem.id}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    await showSuccess("Removido", "Contrato de planejamento removido com sucesso.");
                    fetchDetalhes();
                } else {
                    const err = await response.json();
                    showError("Erro", err.message || "Erro ao remover contrato.");
                }
            } else {
                const response = await fetch(`${API_BASE_URL}/empreendimentos/${id}/arquivo`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ tipo: currentItem.tipo })
                });

                if (response.ok) {
                    await showSuccess("Removido", `${currentItem.label} removido com sucesso.`);
                    fetchDetalhes();
                    setFluxoIndex(0);
                } else {
                    const err = await response.json();
                    showError("Erro", err.message || "Erro ao remover arquivo.");
                }
            }
        } catch (error) {
            showError("Conexão", "Erro de conexão ao tentar excluir.");
        }
    };

    // --- HELPERS ORIGINAIS ---
    const formatMoney = (value) => {
        if (!value) return 'R$ 0,00';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    const formatData = (isoString) => {
        if (!isoString) return '-';
        const date = new Date(isoString);
        return date.toLocaleDateString('pt-BR') + ' às ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const formatLastUpdate = (dateString) => {
        let actualDateString = dateString;
        if (!actualDateString && metasEditaveis && metasEditaveis.length > 0) {
            let maxTime = 0;
            metasEditaveis.forEach(row => {
                const d = row.atualizado_em || row.criado_em;
                if (d) {
                    const t = new Date(d).getTime();
                    if (t > maxTime) {
                        maxTime = t;
                        actualDateString = d;
                    }
                }
            });
        }

        if (!actualDateString) return 'Maio/25';
        const matches = String(actualDateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (matches) {
            const [, year, month, day] = matches;
            return `${day}/${month}/${year}`;
        }
        const date = new Date(actualDateString);
        if (isNaN(date.getTime())) return 'Maio/25';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const getTimestamp = (d) => {
        if (!d) return 9999999;
        const m = { 'Jan': 0, 'Fev': 1, 'Mar': 2, 'Abr': 3, 'Mai': 4, 'Jun': 5, 'Jul': 6, 'Ago': 7, 'Set': 8, 'Out': 9, 'Nov': 10, 'Dez': 11 };
        const p = d.split(' ');
        return p.length < 2 ? 9999999 : (parseInt(p[1]) * 100) + (m[p[0]] || 0);
    };

    const getFileUrl = (url) => {
        if (!url) return null;
        const t = new Date().getTime();
        if (url.startsWith('http')) return url;
        return `${SERVER_URL}${url.startsWith('/') ? url : '/' + url}?t=${t}`;
    };

    const getControleLabel = (valor) => {
        if (valor === 'Sim') return <span className="text-emerald-600 font-bold">Sim</span>;
        if (valor === 'Não' || valor === 'Nao') return <span className="text-red-500 font-bold">Não</span>;
        return <span className="text-gray-400 font-medium italic text-xs">Não Informado</span>;
    };

    const totais = useMemo(() => {
        if (!data || !data.contratos) return { valor: 0, qtd: 0 };
        const valorTotal = data.contratos.reduce((acc, c) => acc + (Number(c.valorContratoCadastroETE) || 0), 0);
        return { valor: valorTotal, qtd: data.contratos.length };
    }, [data]);

    const renderTopicos = (texto) => {
        if (!texto) return <span className="text-gray-400 italic text-sm">Informação não disponível.</span>;
        return (
            <ul className="space-y-2 mt-2">
                {texto.split(/[.\n]+/).filter(i => i.trim().length > 0).map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed">
                        <span className="mt-1.5 w-1.5 h-1.5 bg-sabesp-blue rounded-full flex-shrink-0"></span><span>{item.trim()}.</span>
                    </li>
                ))}
            </ul>
        );
    };

    const renderFluxogramaContent = () => {
        const items = [];
        if (data?.fluxograma_url) items.push({ type: 'image', url: getFileUrl(data.fluxograma_url), label: 'Fluxograma' });
        if (data?.fluxograma_video_url) items.push({ type: 'video', url: getFileUrl(data.fluxograma_video_url), label: 'Vídeo' });

        if (items.length === 0) return (
            <div className="h-64 flex flex-col items-center justify-center text-gray-500 gap-4 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                <span className="text-4xl opacity-20">📐</span>
                <p className="text-sm font-medium">Nenhum arquivo.</p>
                {isAdmin && <button onClick={() => setIsFluxoModalOpen(true)} className="text-sabesp-cyan hover:underline text-xs font-bold">Adicionar</button>}
            </div>
        );

        const currentIndex = items[fluxoIndex] ? fluxoIndex : 0;
        const currentItem = items[currentIndex];

        return (
            <div className="relative w-full h-full group bg-gray-900 flex items-center justify-center overflow-hidden rounded-2xl border border-gray-200 shadow-inner">
                {currentItem.type === 'image' && (
                    <div className="relative w-full h-full bg-white flex items-center justify-center">
                        <img src={currentItem.url} alt="Fluxo" className="max-w-full max-h-full object-contain" />
                        <button onClick={() => window.open(currentItem.url, '_blank')} className="absolute bottom-4 right-4 bg-white text-gray-800 px-4 py-2 rounded-full text-xs font-bold shadow-lg hover:scale-105 transition-all flex items-center gap-2 z-20">🔍 Tela Cheia</button>
                    </div>
                )}
                {currentItem.type === 'video' && (
                    <div className="w-full h-full bg-black flex items-center justify-center">
                        <video src={currentItem.url} controls autoPlay loop playsInline className="w-full h-full max-h-[500px]">Vídeo não suportado.</video>
                    </div>
                )}

                {isAdmin && (
                    <button
                        onClick={() => handleDeleteArquivo(currentItem.label)}
                        className="absolute top-4 right-4 bg-white/90 hover:bg-red-50 text-red-500 hover:text-red-600 w-10 h-10 rounded-full flex items-center justify-center shadow-lg backdrop-blur-sm z-30 transition-all duration-300 transform hover:scale-110 border border-red-100"
                    >
                        🗑️
                    </button>
                )}

                {items.length > 1 && (
                    <>
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider backdrop-blur-md z-10 border border-white/10">
                            {currentItem.label}
                        </div>
                        <button onClick={() => setFluxoIndex(prev => (prev === 0 ? items.length - 1 : prev - 1))} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white text-gray-700 rounded-full flex items-center justify-center shadow-lg z-20 transition-all hover:scale-110">❮</button>
                        <button onClick={() => setFluxoIndex(prev => (prev === items.length - 1 ? 0 : prev + 1))} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white text-gray-700 rounded-full flex items-center justify-center shadow-lg z-20 transition-all hover:scale-110">❯</button>
                    </>
                )}
            </div>
        );
    };

    if (loading) return (
        <div className="flex flex-col h-screen items-center justify-center bg-[#F8F9FA] gap-4">
            <div className="w-12 h-12 border-4 border-sabesp-blue border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-500 font-medium tracking-wide animate-pulse">Carregando dados do empreendimento...</p>
        </div>
    );
    if (!data) return (
        <div className="flex flex-col h-screen items-center justify-center bg-[#F8F9FA] gap-4">
            <span className="text-5xl opacity-40">🏢</span>
            <p className="text-gray-500 font-medium tracking-wide">Empreendimento não encontrado.</p>
            <button onClick={() => navigate('/')} className="mt-4 px-6 py-2 bg-sabesp-blue text-white rounded-full font-bold shadow-md hover:bg-blue-700 transition-colors">Voltar ao Início</button>
        </div>
    );

    return (<>
        <div className="page-container relative bg-[#F8F9FA] min-h-screen pb-20 font-sans">

            {/* HEADER PREMIUM */}
            <div className="relative w-full min-h-[350px] md:h-[350px] bg-slate-900 overflow-hidden shadow-2xl pb-8 md:pb-0 flex flex-col justify-end">
                {data.imagem_capa_url ? (
                    <div className="absolute inset-0 bg-cover bg-center opacity-60 mix-blend-luminosity hover:mix-blend-normal hover:opacity-80 transition-all duration-1000 ease-in-out" style={{ backgroundImage: `url('${getFileUrl(data.imagem_capa_url)}')` }}></div>
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-sabesp-blue opacity-80"></div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#B9BFF0]/40 via-[#48C3F0]/30 to-transparent"></div>
                <div className="relative md:absolute md:inset-0 container mx-auto px-6 pt-24 md:pt-0 flex flex-col justify-end pb-4 md:pb-12 z-10">
                    <button onClick={() => navigate('/')} className="absolute top-8 left-6 text-white/90 hover:text-white flex items-center gap-2 text-sm font-bold bg-white/10 hover:bg-white/20 px-5 py-2.5 rounded-full backdrop-blur-md transition-all border border-white/10 shadow-lg group">
                        <span className="group-hover:-translate-x-1 transition-transform">←</span> Voltar
                    </button>
                    <div className="max-w-4xl animate-slideUp">
                        {data.percentual_conclusao !== undefined && data.percentual_conclusao !== null && Number(data.percentual_conclusao) > 0 ? (
                            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/20 text-white text-xs font-black w-fit mb-4 pt-14 md:pt-0 animate-fadeIn">
                                <span>Progresso da Obra:</span>
                                <span className="text-sabesp-cyan font-mono">{Number(data.percentual_conclusao)}%</span>
                                <div className="w-16 h-1.5 bg-white/20 rounded-full overflow-hidden">
                                    <div className="h-full bg-sabesp-cyan" style={{ width: `${data.percentual_conclusao}%` }}></div>
                                </div>
                            </div>
                        ) : (
                            <div className="pt-14 md:pt-0"></div>
                        )}
                        <h1 className="text-2xl sm:text-3xl md:text-6xl font-black text-white mb-4 tracking-tight drop-shadow-2xl leading-tight">{data.nome}</h1>
                        <p className="text-blue-100/90 text-sm md:text-lg font-light leading-relaxed max-w-3xl drop-shadow-md border-l-4 border-sabesp-cyan pl-5 mb-8 line-clamp-3">{data.descricao_curta}</p>
                        {isAdmin && (<button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm font-bold transition-all border border-white/20 backdrop-blur-md shadow-xl hover:scale-105 active:scale-95 w-fit">⚙️ Gerenciar Obra</button>)}
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-6 -mt-10 relative z-20">
                {/* CARDS COM DESIGN PREMIUM E GLASSMORPHISM */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
                    {/* Card Vazão */}
                    <div className="bg-white/80 backdrop-blur-md p-4 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 border border-white hover:-translate-y-1 group  shadow-sm border-l-4 border-l-[#00AEEF]">
                        <div className="flex gap-3 mb-4 items-center">
                            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-sky-100 to-blue-50 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform">
                                <span className="text-xl">💧</span>
                            </div>
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Vazão (L/s)</span>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between items-end border-b border-gray-100 pb-2">
                                <span className="text-xs text-gray-500 font-medium">Atual</span>
                                <span className="text-sm font-black text-sky-600">{data.vazao_atual_ls || '-'}</span>
                            </div>
                            <div className="flex justify-between items-end pt-1">
                                <span className="text-xs text-gray-400">Futura</span>
                                <span className="text-sm font-bold text-gray-400">{data.vazao_futura_ls || '-'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Card Controle de Vazão */}
                    <div className="bg-white/80 backdrop-blur-md p-4 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 border border-white hover:-translate-y-1 group shadow-sm border-l-4 border-l-[#F59E0B]">
                        <div className="flex gap-3 mb-4 items-center">
                            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-100 to-yellow-50 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform">
                                <span className="text-xl">🎛️</span>
                            </div>
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Controle</span>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between items-end border-b border-gray-100 pb-2">
                                <span className="text-xs text-gray-500 font-medium">Entrada</span>
                                <span className="text-sm">{getControleLabel(data.controle_vazao_entrada)}</span>
                            </div>
                            <div className="flex justify-between items-end pt-1">
                                <span className="text-xs text-gray-400">Saída</span>
                                <span className="text-sm">{getControleLabel(data.controle_vazao_saida)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Card População */}
                    <div className="bg-white/80 backdrop-blur-md p-5 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 border border-white hover:-translate-y-1 group shadow-sm border-l-4 border-l-[#8B5CF6]">
                        <div className="flex gap-3 mb-4 items-center">
                            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-purple-100 to-fuchsia-50 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform">
                                <span className="text-xl">👥</span>
                            </div>
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">População</span>
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between items-end border-b border-gray-100 pb-2">
                                <span className="text-xs text-gray-500 font-medium">Atual</span>
                                <span className="text-sm font-black text-gray-800">{data.populacao_atual?.toLocaleString() || '-'}</span>
                            </div>
                            <div className="flex justify-between items-end pt-1">
                                <span className="text-xs text-gray-400">Futura</span>
                                <span className="text-sm font-bold text-purple-500">{data.populacao_futura?.toLocaleString() || '-'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Card Investimento */}
                    <div className="bg-white/80 backdrop-blur-md p-5 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 border border-white hover:-translate-y-1 group shadow-sm border-l-4 border-l-[#0C336A]">
                        <div className="flex gap-3 mb-4 items-center">
                            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-slate-200 to-gray-50 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform">
                                <span className="text-xl">💰</span>
                            </div>
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Investimento</span>
                        </div>
                        <div className="mt-auto pt-2">
                            <p className="text-sm font-black text-slate-800 truncate">{formatMoney(totais.valor)}</p>
                            <p className="text-xs font-medium text-gray-400 mt-1 bg-slate-100 inline-block px-2 py-0.5 rounded-md">{totais.qtd} contratos</p>
                        </div>
                    </div>

                    {/* Card Corpo Hídrico */}
                    <div className="bg-white/80 backdrop-blur-md p-5 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 border border-white hover:-translate-y-1 group shadow-sm border-l-4 border-l-[#10B981]">
                        <div className="flex gap-3 mb-4 items-center">
                            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-100 to-teal-50 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform">
                                <span className="text-xl">🌊</span>
                            </div>
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Corpo Hídrico</span>
                        </div>
                        <div className="mt-auto pt-2">
                            <p className="text-sm font-bold text-gray-800 line-clamp-2" title={data.corpo_hidrico}>{data.corpo_hidrico || "N/A"}</p>
                            <span className="text-[10px] font-bold bg-teal-50 text-teal-700 px-2 py-1 rounded-md border border-teal-100 mt-2 inline-block">
                                {data.classe_rio || 'Sem classe'}
                            </span>
                        </div>
                    </div>

                    {/* Card Local */}
                    <div className="bg-white/80 backdrop-blur-md p-5 rounded-3xl shadow-sm hover:shadow-xl transition-all duration-300 border border-white hover:-translate-y-1 group shadow-sm border-l-4 border-l-[#EF4444]">
                        <div className="flex gap-3 mb-4 items-center">
                            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-red-100 to-rose-50 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform">
                                <span className="text-xl">📍</span>
                            </div>
                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Localização</span>
                        </div>
                        <div className="mt-auto pt-2">
                            <p className="text-sm font-bold text-gray-800 truncate">{data.nome_municipio || '-'}</p>
                            <p className="text-[10px] text-gray-400 line-clamp-2 mt-1 font-medium leading-snug" title={data.endereco}>
                                {data.endereco || "Endereço não informado"}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-sm border border-gray-200 p-1.5 flex gap-1 w-full md:w-fit mb-8 relative z-20">
                    {['Visão Geral', 'Contratos e Execução'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => {
                                setActiveTab(tab);
                                // 📊 REGISTRO DE TELEMETRIA NA TROCA DE ABA
                                try {
                                    fetch(`${API_BASE_URL}/telemetria/log`, {
                                        method: 'POST',
                                        headers: {
                                            'Content-Type': 'application/json',
                                            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                                        },
                                        body: JSON.stringify({
                                            empreendimento_id: id,
                                            aba_acessada: tab === 'Visão Geral' ? 'visao_geral' : 'contratos'
                                        })
                                    }).catch(() => {});
                                } catch (e) {}
                            }}
                            className={`flex-1 md:flex-none px-8 py-3 rounded-xl text-sm font-bold transition-all duration-300 ${activeTab === tab ? 'bg-gradient-to-r from-sabesp-blue to-blue-600 text-white shadow-lg scale-[1.02]' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'}`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                <div className="min-h-[400px]">
                    {activeTab === 'Visão Geral' && (
                        <div className="space-y-8 animate-fadeIn py-4">
                            {data.descricao_completa && data.descricao_completa.trim() !== "" && (
                                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2"><span>📝</span> Descrição Completa</h4>
                                    <p className="text-gray-600 leading-loose text-justify font-light text-sm">{data.descricao_completa}</p>
                                </div>
                            )}

                            {data.historia_narrativa && data.historia_narrativa.trim() !== "" && (
                                <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2"><span>📖</span> Concepção de Tratamento</h4>
                                    <p className="text-gray-600 leading-loose text-justify font-light text-sm">{data.historia_narrativa}</p>
                                </div>
                            )}

                            {(() => {
                                const hasSustentabilidade = (data.texto_sustentabilidade && data.texto_sustentabilidade.trim() !== "") || (data.sustentabilidade && String(data.sustentabilidade).trim() !== "");
                                return (
                                    <div className={`grid grid-cols-1 ${hasSustentabilidade ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
                                        <div className="bg-sky-50/30 border border-sky-100 p-8 rounded-3xl">
                                            <div className="flex items-center gap-2 mb-4">
                                                <span className="bg-sky-100 p-1.5 rounded-lg text-lg">⚡</span>
                                                <h4 className="font-bold text-sky-900 text-sm uppercase tracking-wide">Diferenciais</h4>
                                            </div>
                                            <div className="text-sm text-sky-900/80 leading-relaxed">
                                                {renderTopicos(data.texto_diferenciais || data.texto_tecnologia)}
                                            </div>
                                        </div>
                                        <div className="bg-emerald-50/30 border border-emerald-100 p-8 rounded-3xl">
                                            <div className="flex items-center gap-2 mb-4">
                                                <span className="bg-emerald-100 p-1.5 rounded-lg text-lg">👥</span>
                                                <h4 className="font-bold text-emerald-900 text-sm uppercase tracking-wide">Benefícios</h4>
                                            </div>
                                            <div className="text-sm text-emerald-900/80 leading-relaxed">
                                                {renderTopicos(data.texto_beneficios)}
                                            </div>
                                        </div>
                                        {hasSustentabilidade && (
                                            <div className="bg-teal-50/30 border border-teal-100 p-8 rounded-3xl">
                                                <div className="flex items-center gap-2 mb-4">
                                                    <span className="bg-teal-100 p-1.5 rounded-lg text-lg">🌱</span>
                                                    <h4 className="font-bold text-teal-900 text-sm uppercase tracking-wide">Sustentabilidade</h4>
                                                </div>
                                                <div className="text-sm text-teal-900/80 leading-relaxed">
                                                    {renderTopicos(data.texto_sustentabilidade || data.sustentabilidade)}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {data && (isAdmin || !!data.exibir_planejamento_publico) && (
                                <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden transition-all duration-300">
                                    <div
                                        onClick={() => setIsPlanejamentoExpanded(!isPlanejamentoExpanded)}
                                        className="bg-slate-50/60 hover:bg-slate-50 p-6 flex flex-col md:flex-row md:items-center justify-between cursor-pointer transition-colors select-none gap-4"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`w-8 h-8 rounded-full bg-white border border-gray-200 text-gray-500 flex items-center justify-center font-bold text-sm transition-transform duration-300 shadow-sm ${isPlanejamentoExpanded ? 'rotate-90 bg-sabesp-blue text-white border-sabesp-blue' : ''}`}>
                                                ›
                                            </div>
                                            <div>
                                                <h3 className="text-base font-bold text-gray-800">Projeção de Economias Encaminhadas para Tratamento</h3>
                                                <p className="text-xs text-gray-400 font-medium mt-0.5">Fonte: Consórcio TPF Engecorps - Gerenciamento Obras Lineares</p>
                                                <div
                                                    className="flex items-center gap-1.5 text-[10px] text-gray-400 font-semibold mt-1"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <span>Última atualização em:</span>
                                                    {isAdmin ? (
                                                        <input
                                                            type="text"
                                                            value={dataAtuInput}
                                                            onChange={(e) => setDataAtuInput(e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onBlur={() => handleSalvarDataPlanejamento(dataAtuInput)}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                                            placeholder="Ex: Junho/26 ou 23/06/2026"
                                                            className="bg-white border border-gray-200 focus:border-sabesp-blue rounded-xl px-3 py-1 text-[10px] font-bold text-slate-700 outline-none transition-all shadow-inner w-44 text-center cursor-text"
                                                        />
                                                    ) : (
                                                        <span>{data.planejamento_ultima_atualizacao ? data.planejamento_ultima_atualizacao : formatLastUpdate(data.planejamento?.ultima_atualizacao)}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-1 border-l-2 border-gray-100 pl-4 flex-1 max-w-md">
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded flex items-center gap-1.5">
                                                    <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                                                    Total de economias Encaminhadas:
                                                </span>
                                                <span className="font-mono font-black text-emerald-800 text-sm">
                                                    {somasCalculadas.consolidado ? somasCalculadas.consolidado.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 }) : '-'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-slate-600 font-semibold bg-slate-100 px-2 py-0.5 rounded flex items-center gap-1.5">
                                                    <span className="w-2 h-2 bg-slate-400 rounded-full"></span>
                                                    Total de economias Projetadas:
                                                </span>
                                                <span className="font-mono font-black text-slate-700 text-sm">
                                                    {somasCalculadas.projecao ? somasCalculadas.projecao.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 }) : '-'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-blue-700 font-bold bg-blue-50 px-2 py-0.5 rounded flex items-center gap-1.5">
                                                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                                    Total de economias - {new Date().getFullYear()}:
                                                </span>
                                                <span className="font-mono font-black text-blue-800 text-sm">
                                                    {somasCalculadas.consolidadoAnoAtual ? somasCalculadas.consolidadoAnoAtual.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 }) : '-'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-purple-700 font-bold bg-purple-50 px-2 py-0.5 rounded flex items-center gap-1.5">
                                                    <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                                                    Total de Economias:
                                                </span>
                                                <span className="font-mono font-black text-purple-800 text-sm">
                                                    {((somasCalculadas.consolidado || 0) + (somasCalculadas.projecao || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 })}
                                                </span>
                                            </div>

                                            {/* Indicador Discreto de Contratos */}
                                            <div className="flex justify-between items-center text-[10px] mt-1 pt-1 border-t border-gray-100/60">
                                                <span className="text-gray-400 font-bold uppercase tracking-wider">
                                                    Contratos na Tabela:
                                                </span>
                                                <span className="font-bold text-gray-500">
                                                    {metasEditaveis.length} {metasEditaveis.length === 1 ? 'contrato' : 'contratos'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    {isPlanejamentoExpanded && (
                                        <div className="border-t border-gray-100 bg-white animate-fadeIn overflow-hidden">
                                            {(() => {
                                                const chartColumns = [];
                                                const currentYear = new Date().getFullYear();
                                                // Meses do ano atual
                                                for (let m = 1; m <= 12; m++) {
                                                    chartColumns.push(`${currentYear}-${String(m).padStart(2, '0')}`);
                                                }
                                                // Anos futuros (passa a chave legada para o gráfico consolidar)
                                                Object.keys(colunasAgrupadas).forEach(ano => {
                                                    if (Number(ano) > currentYear) {
                                                        chartColumns.push(`ano${ano}`);
                                                    }
                                                });

                                                return (
                                                    <div className="p-6 border-b border-gray-100 bg-slate-50/20">
                                                        <PlanejamentoChart
                                                            colunasMeses={chartColumns}
                                                            metasEditaveis={metasEditaveis}
                                                            somaMensal={somaMensal}
                                                        />
                                                    </div>
                                                );
                                            })()}
                                            {isAdmin && (
                                                <div className="flex justify-end px-6 py-2 border-b border-gray-100 bg-white">
                                                    <button 
                                                        onClick={handleAddNextYear}
                                                        className="px-2 py-1 text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 font-semibold rounded border border-blue-200 flex items-center gap-1"
                                                    >
                                                        + Adicionar Ano
                                                    </button>
                                                </div>
                                            )}
                                            {isAdmin ? (
                                                <React.Fragment key="admin-planning">
                                                    <div className="w-full overflow-x-auto max-h-[600px] scrollbar-thin">
                                                        <table className="w-full text-left border-collapse text-xs relative min-w-[700px]">
                                                            <thead>
                                                                <tr className="bg-slate-100 text-slate-700 font-bold uppercase tracking-wider border-b border-gray-200">
                                                                    <th className="p-3 pl-6 w-[140px] min-w-[140px] max-w-[140px] sticky top-0 left-0 bg-slate-100 z-40 border-r border-slate-200">CONTRATO ID</th>
                                                                    <th className="p-3 w-[180px] min-w-[180px] max-w-[180px] sticky top-0 left-[140px] bg-slate-100 z-40 border-r border-slate-200">OBJETO REDUZIDO</th>
                                                                    {Object.entries(colunasAgrupadas).map(([ano, colunas]) => {
                                                                        const isExpanded = expandedYears[ano];
                                                                        const canExpand = colunas.length > 1; // Só pode expandir se tiver meses

                                                                        if (!isExpanded && canExpand) {
                                                                            return (
                                                                                <th key={`total-${ano}`} className="p-3 text-center min-w-[120px] font-mono sticky top-0 bg-slate-100 z-30 border-b border-gray-200">
                                                                                    <div className="flex items-center justify-center gap-1.5">
                                                                                        <span>Total {ano}</span>
                                                                                        <button onClick={() => {
                                                                                            setExpandedYears(prev => ({...prev, [ano]: true}));
                                                                                            if (somaMensal[`ano${ano}`] > 0) {
                                                                                                showWarning(`O total de ${ano} será recalculado com base na soma dos meses preenchidos.`);
                                                                                            }
                                                                                        }} className="text-sabesp-blue font-black text-lg hover:scale-110 transition-transform" title={`Expandir meses de ${ano}`}>
                                                                                            +
                                                                                        </button>
                                                                                        {isAdmin && (
                                                                                            <button
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleOpenDeleteYearModal(ano);
                                                                                                }}
                                                                                                className="text-[11px] opacity-60 hover:opacity-100 hover:scale-125 transition-all text-red-500 ml-1 cursor-pointer"
                                                                                                title={`Excluir o ano ${ano} do planejamento`}
                                                                                            >
                                                                                                🗑️
                                                                                            </button>
                                                                                        )}
                                                                                    </div>
                                                                                </th>
                                                                            );
                                                                        }

                                                                        return (
                                                                            <React.Fragment key={`group-${ano}`}>
                                                                                {colunas.map((mes, index) => (
                                                                                    <th key={mes} className="p-3 text-center min-w-[90px] font-mono sticky top-0 bg-slate-100 z-30 border-b border-gray-200">
                                                                                        <div className="flex items-center justify-center gap-1">
                                                                                            {index === 0 && canExpand && (
                                                                                                <>
                                                                                                    <button onClick={() => setExpandedYears(prev => ({...prev, [ano]: false}))} className="text-gray-400 hover:text-sabesp-blue text-sm font-black mr-1 transition-transform hover:scale-110" title={`Recolher ano ${ano}`}>
                                                                                                        -
                                                                                                    </button>
                                                                                                    {isAdmin && (
                                                                                                        <button
                                                                                                            onClick={(e) => {
                                                                                                                e.stopPropagation();
                                                                                                                handleOpenDeleteYearModal(ano);
                                                                                                            }}
                                                                                                            className="text-[11px] opacity-60 hover:opacity-100 hover:scale-125 transition-all text-red-500 mr-1 cursor-pointer"
                                                                                                            title={`Excluir o ano ${ano} do planejamento`}
                                                                                                        >
                                                                                                            🗑️
                                                                                                        </button>
                                                                                                    )}
                                                                                                </>
                                                                                            )}
                                                                                            <span>{formatPeriodHeader(mes)}</span>
                                                                                            {isAdmin && (
                                                                                                <button
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        handleOpenMonthEdit(mes);
                                                                                                    }}
                                                                                                    className="text-[10px] opacity-40 hover:opacity-100 hover:scale-110 transition-all cursor-pointer"
                                                                                                    title="Editar todos deste mês"
                                                                                                >
                                                                                                    ✏️
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                    </th>
                                                                                ))}
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                                    {isPlanejamentoExpanded && isAdmin && <th className="p-3 text-center min-w-[130px] bg-slate-200/50 sticky top-0 right-0 z-30 border-b border-gray-200">AÇÃO</th>}
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-100 font-medium text-gray-700">
                                                                {/* Soma Mensal Row */}
                                                                {filteredMetas.length > 0 && (
                                                                    <tr className="bg-slate-100/90 font-bold border-b border-slate-200 select-none">
                                                                        <td className="p-2 pl-6 sticky left-0 bg-slate-100 z-20 border-r border-slate-200 font-black text-gray-800 w-[140px] min-w-[140px] max-w-[140px]">
                                                                            SOMA MENSAL
                                                                        </td>
                                                                        <td className="p-2 sticky left-[140px] bg-slate-100 z-20 border-r border-slate-200 font-black text-gray-800 w-[180px] min-w-[180px] max-w-[180px] truncate">
                                                                            -
                                                                        </td>
                                                                        {Object.entries(colunasAgrupadas).map(([ano, colunas]) => {
                                                                            const isExpanded = expandedYears[ano];
                                                                            const canExpand = colunas.length > 1;

                                                                            if (!isExpanded && canExpand) {
                                                                                const sumVal = colunas.reduce((acc, c) => acc + (somaMensal[c] || 0), 0);
                                                                                const legacyVal = somaMensal[`ano${ano}`] || 0;
                                                                                const finalTotal = sumVal > 0 ? sumVal : legacyVal;
                                                                                return (
                                                                                    <td key={`total-${ano}`} className="p-2 text-center font-mono font-black text-slate-800 bg-slate-200/80 border-x border-slate-300">
                                                                                        {finalTotal === 0 ? '-' : finalTotal.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 })}
                                                                                    </td>
                                                                                );
                                                                            }

                                                                            return colunas.map(mesChave => {
                                                                                const val = somaMensal[mesChave] || 0;
                                                                                return (
                                                                                    <td key={mesChave} className="p-2 text-center font-mono font-black text-slate-800 bg-slate-100/70">
                                                                                        {val === 0 ? '-' : val.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 })}
                                                                                    </td>
                                                                                );
                                                                            });
                                                                        })}
                                                                        {isPlanejamentoExpanded && isAdmin && <td className="p-2 bg-slate-100/50 min-w-[130px]"></td>}
                                                                    </tr>
                                                                )}
                                                                {filteredMetas.length > 0 ? (
                                                                    filteredMetas.map((meta) => (
                                                                        <tr key={meta.id} className="group hover:bg-slate-50/80 transition-colors">
                                                                            <td className="p-2 pl-6 sticky left-0 bg-white group-hover:bg-slate-50 z-20 border-r border-gray-200 w-[140px] min-w-[140px] max-w-[140px]">
                                                                                {(isPlanejamentoExpanded && isAdmin) ? (
                                                                                    <input
                                                                                        type="text"
                                                                                        value={meta.contrato_id || ''}
                                                                                        onChange={(e) => handleInputChange(meta.id, 'contrato_id', e.target.value)}
                                                                                        className="w-full bg-transparent font-mono font-bold text-sabesp-blue outline-none border-b border-transparent focus:border-sabesp-blue p-1"
                                                                                        placeholder="ID Contrato"
                                                                                    />
                                                                                ) : (
                                                                                    <span className="font-mono font-bold text-sabesp-blue px-1">
                                                                                        {meta.contrato_id || '-'}
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            <td className="p-2 sticky left-[140px] bg-white group-hover:bg-slate-50 z-20 border-r border-gray-200 w-[180px] min-w-[180px] max-w-[180px] truncate">
                                                                                {(isPlanejamentoExpanded && isAdmin) ? (
                                                                                    <input
                                                                                        type="text"
                                                                                        value={meta.objeto_reduzido || ''}
                                                                                        onChange={(e) => handleInputChange(meta.id, 'objeto_reduzido', e.target.value)}
                                                                                        className="w-full bg-transparent text-gray-800 outline-none border-b border-transparent focus:border-sabesp-blue p-1 truncate"
                                                                                    />
                                                                                ) : (
                                                                                    <span className="text-gray-800 px-1 truncate block max-w-[180px]" title={meta.objeto_reduzido}>
                                                                                        {meta.objeto_reduzido || '-'}
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            {Object.entries(colunasAgrupadas).map(([ano, colunas]) => {
                                                                                const isExpanded = expandedYears[ano];
                                                                                const canExpand = colunas.length > 1;

                                                                                if (!isExpanded && canExpand) {
                                                                                    const sumVal = colunas.reduce((acc, c) => acc + Number(meta[c] || 0), 0);
                                                                                    const legacyVal = Number(meta[`ano${ano}`] || 0);
                                                                                    const finalTotal = sumVal > 0 ? sumVal : legacyVal;
                                                                                    const displayVal = finalTotal === 0 ? '-' : finalTotal.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 });
                                                                                    return (
                                                                                        <td key={`total-${ano}`} className="px-3 py-2 text-center text-sm font-medium text-slate-700 whitespace-nowrap border-x border-slate-200" style={{ backgroundColor: '#f8fafc' }}>
                                                                                            <div className="flex items-center justify-center gap-1.5 group cursor-pointer" onClick={() => isAdmin && handleOpenCellEdit(meta.id, `ano${ano}`, finalTotal, 'Projeção')}>
                                                                                                <span>{displayVal}</span>
                                                                                                {isAdmin && (
                                                                                                    <button className="text-slate-400 hover:text-blue-600 transition-colors opacity-80 group-hover:opacity-100">
                                                                                                        <PencilIcon className="w-3.5 h-3.5"/>
                                                                                                    </button>
                                                                                                )}
                                                                                            </div>
                                                                                        </td>
                                                                                    );
                                                                                }

                                                                                return colunas.map(mesChave => {
                                                                                    const val = Number(meta[mesChave] || 0);
                                                                                    const statusKey = getDbStatusKey(mesChave);
                                                                                    const status = meta[statusKey] || 'Projeção';
                                                                                    const isConsolidado = status === 'Consolidado';
                                                                                    const cellBg = isConsolidado ? '#e6f4ea' : '#f1f3f4';
                                                                                    const cellFg = isConsolidado ? '#137333' : '#3c4043';
                                                                                    const displayVal = val === 0 ? '-' : val.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 });
                                                                                    return (
                                                                                        <td
                                                                                            key={mesChave}
                                                                                            className={`p-1 text-center select-none font-mono font-bold transition-all ${isAdmin ? 'cursor-pointer hover:brightness-95' : ''}`}
                                                                                            style={{ backgroundColor: cellBg, color: cellFg }}
                                                                                            onClick={() => isAdmin && handleOpenCellEdit(meta.id, mesChave, val, status)}
                                                                                        >
                                                                                            <div className="flex items-center justify-center gap-1">
                                                                                                <span>{displayVal}</span>
                                                                                                {isAdmin && isPlanejamentoExpanded && (
                                                                                                    <span className="text-[10px] opacity-40 hover:opacity-100 transition-opacity">✏️</span>
                                                                                                )}
                                                                                            </div>
                                                                                        </td>
                                                                                    );
                                                                                });
                                                                            })}
                                                                            {isPlanejamentoExpanded && isAdmin && (
                                                                                <td className="p-2 text-center bg-slate-50/50 sticky right-0 z-20 min-w-[130px]">
                                                                                    <div className="flex items-center justify-center gap-1.5">
                                                                                        <button
                                                                                            onClick={() => handleSalvarMetaManual(meta)}
                                                                                            disabled={salvandoMetaId === meta.id}
                                                                                            className={`w-8 h-8 flex items-center justify-center text-white font-bold rounded-lg shadow-sm transition-all ${salvandoMetaId === meta.id ? 'bg-gray-400 cursor-not-allowed' : 'bg-sabesp-blue hover:bg-blue-700'}`}
                                                                                            title="Salvar contrato e objeto"
                                                                                        >
                                                                                            {salvandoMetaId === meta.id ? '...' : '💾'}
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={() => handleOpenRowEdit(meta)}
                                                                                            className="w-8 h-8 flex items-center justify-center bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg shadow-sm transition-all"
                                                                                            title="Editar toda a linha"
                                                                                        >
                                                                                            ✏️
                                                                                        </button>
                                                                                        <button
                                                                                            onClick={() => handleDeletePlanejamento(meta)}
                                                                                            className="w-8 h-8 flex items-center justify-center bg-rose-500 hover:bg-rose-600 text-white font-bold rounded-lg shadow-sm transition-all"
                                                                                            title="Excluir contrato de planejamento"
                                                                                        >
                                                                                            🗑️
                                                                                        </button>
                                                                                    </div>
                                                                                </td>
                                                                            )}
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan={2 + colunasMeses.length + (isPlanejamentoExpanded ? 1 : 0)} className="p-8 text-center text-gray-400 italic">
                                                                            Nenhuma estrutura de planejamento encontrada para esta ETE no banco de dados.
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                                {isPlanejamentoExpanded && novaLinhasMeta.map((novaLinha) => (
                                                                    <tr key={novaLinha._tempId} className="bg-yellow-50/60 border-l-4 border-l-yellow-400">
                                                                        <td className="p-2 pl-6 sticky left-0 bg-yellow-50/80 z-20 border-r border-yellow-200 w-[140px] min-w-[140px] max-w-[140px]">
                                                                            <input
                                                                                type="text"
                                                                                value={novaLinha.contrato_id || ''}
                                                                                onChange={(e) => handleInputNovaLinha(novaLinha._tempId, 'contrato_id', e.target.value)}
                                                                                placeholder="ID Contrato"
                                                                                className="w-full bg-white border border-yellow-300 rounded-lg font-mono font-bold text-sabesp-blue outline-none focus:border-sabesp-blue px-2 py-1 text-xs"
                                                                            />
                                                                        </td>
                                                                        <td className="p-2 sticky left-[140px] bg-yellow-50/80 z-20 border-r border-yellow-200 w-[180px] min-w-[180px] max-w-[180px]">
                                                                            <input
                                                                                type="text"
                                                                                value={novaLinha.objeto_reduzido}
                                                                                onChange={(e) => handleInputNovaLinha(novaLinha._tempId, 'objeto_reduzido', e.target.value)}
                                                                                placeholder="Objeto Reduzido"
                                                                                className="w-full bg-white border border-yellow-300 rounded-lg text-gray-800 outline-none focus:border-sabesp-blue px-2 py-1 text-xs"
                                                                            />
                                                                        </td>
                                                                        {colunasMeses.map((mesChave) => (
                                                                            <td key={mesChave} className="p-1 text-center">
                                                                                <input
                                                                                    type="number"
                                                                                    value={novaLinha[mesChave] === 0 ? '' : novaLinha[mesChave]}
                                                                                    onChange={(e) => handleInputNovaLinha(novaLinha._tempId, mesChave, Number(e.target.value))}
                                                                                    placeholder="0"
                                                                                    className="w-full text-center bg-white border border-yellow-300 rounded-lg font-mono text-gray-800 outline-none focus:border-sabesp-blue py-1 text-xs"
                                                                                />
                                                                            </td>
                                                                        ))}
                                                                        <td className="p-2 text-center">
                                                                            <button
                                                                                onClick={() => handleSalvarNovaLinha(novaLinha)}
                                                                                disabled={salvandoMetaId === novaLinha._tempId}
                                                                                className={`px-3 py-1.5 text-white font-bold rounded-lg text-[11px] shadow-sm transition-all ${salvandoMetaId === novaLinha._tempId ? 'bg-gray-400 cursor-not-allowed' : 'bg-emerald-500 hover:bg-emerald-600'}`}
                                                                                title="Salvar Nova Meta"
                                                                            >
                                                                                {salvandoMetaId === novaLinha._tempId ? '...' : '💾 Salvar'}
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* ➕ BOTÃO ADICIONAR NOVO CONTRATO */}
                                                    {isPlanejamentoExpanded && (
                                                        <div className="p-4 border-t border-dashed border-gray-200 flex items-center gap-4 animate-fadeIn">
                                                            <button
                                                                onClick={handleAdicionarNovaLinha}
                                                                className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-dashed border-emerald-400 text-emerald-700 rounded-xl text-xs font-bold hover:bg-emerald-50 hover:border-emerald-500 transition-all shadow-sm"
                                                            >
                                                                <span className="text-base leading-none">➕</span> Adicionar Novo Contrato
                                                            </button>
                                                        </div>
                                                    )}
                                                </React.Fragment>
                                            ) : (
                                                <div key="summary-planning">
                                                    <div className="overflow-auto max-h-[600px] scrollbar-thin border-t border-gray-100">
                                                        <table className="w-full text-left border-collapse text-xs relative">
                                                            <thead>
                                                                <tr className="bg-slate-100 text-slate-700 font-bold uppercase tracking-wider border-b border-gray-200">
                                                                    <th className="p-3 pl-6 w-[320px] min-w-[320px] max-w-[320px] sticky top-0 left-0 bg-slate-100 z-40 border-r border-slate-200">
                                                                        Tabela Resumo
                                                                    </th>
                                                                    {Object.entries(colunasAgrupadas).map(([ano, colunas]) => {
                                                                        const isExpanded = expandedYears[ano];
                                                                        const canExpand = colunas.length > 1;

                                                                        if (!isExpanded && canExpand) {
                                                                            return (
                                                                                <th key={`total-${ano}`} className="p-3 text-center min-w-[120px] font-mono sticky top-0 bg-slate-100 z-30 border-b border-gray-200">
                                                                                    <div className="flex items-center justify-center gap-2">
                                                                                        <span>Total {ano}</span>
                                                                                        <button onClick={() => setExpandedYears(prev => ({...prev, [ano]: true}))} className="text-sabesp-blue font-black text-lg hover:scale-110 transition-transform" title={`Expandir meses de ${ano}`}>
                                                                                            +
                                                                                        </button>
                                                                                    </div>
                                                                                </th>
                                                                            );
                                                                        }

                                                                        return (
                                                                            <React.Fragment key={`group-${ano}`}>
                                                                                {colunas.map((mes, index) => (
                                                                                    <th key={mes} className="p-3 text-center min-w-[90px] font-mono sticky top-0 bg-slate-100 z-30 border-b border-gray-200">
                                                                                        <div className="flex items-center justify-center gap-1">
                                                                                            {index === 0 && canExpand && (
                                                                                                <button onClick={() => setExpandedYears(prev => ({...prev, [ano]: false}))} className="text-gray-400 hover:text-sabesp-blue text-sm font-black mr-1 transition-transform hover:scale-110" title={`Recolher ano ${ano}`}>
                                                                                                    -
                                                                                                </button>
                                                                                            )}
                                                                                            <span>{formatPeriodHeader(mes)}</span>
                                                                                        </div>
                                                                                    </th>
                                                                                ))}
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-100 font-medium text-gray-700">
                                                                {/* Row 1: Total Encaminhado - Mensal */}
                                                                <tr className="hover:bg-slate-50 transition-colors">
                                                                    <td className="p-3 pl-6 sticky left-0 bg-white hover:bg-slate-50 z-20 border-r border-gray-200 font-bold text-gray-800 w-[320px] min-w-[320px] max-w-[320px]">
                                                                        Total Encaminhado - Mensal
                                                                    </td>
                                                                    {Object.entries(colunasAgrupadas).map(([ano, colunas]) => {
                                                                        const isExpanded = expandedYears[ano];
                                                                        const canExpand = colunas.length > 1;

                                                                        if (!isExpanded && canExpand) {
                                                                            const sumVal = colunas.reduce((acc, col) => {
                                                                                const isConsol = metasEditaveis && metasEditaveis.length > 0 && metasEditaveis.some(row => row[col + '_status'] === 'Consolidado');
                                                                                return acc + (isConsol ? (somaMensal[col] || 0) : 0);
                                                                            }, 0);
                                                                            const legacyVal = (metasEditaveis && metasEditaveis.some(row => row[`ano${ano}_status`] === 'Consolidado')) ? (somaMensal[`ano${ano}`] || 0) : 0;
                                                                            const finalVal = sumVal > 0 ? sumVal : legacyVal;
                                                                            return (
                                                                                <td key={`total-${ano}`} className="p-3 text-center font-mono font-bold text-gray-700 bg-slate-100/50">
                                                                                    {finalVal !== 0 ? finalVal.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 }) : '-'}
                                                                                </td>
                                                                            );
                                                                        }

                                                                        return (
                                                                            <React.Fragment key={`group-${ano}`}>
                                                                                {colunas.map((col) => {
                                                                                    const val = somaMensal[col] || 0;
                                                                                    const isConsol = metasEditaveis && metasEditaveis.length > 0 && metasEditaveis.some(row => row[col + '_status'] === 'Consolidado');
                                                                                    return (
                                                                                        <td key={col} className="p-3 text-center font-mono font-bold text-gray-700">
                                                                                            {isConsol && val !== 0 ? val.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 }) : '-'}
                                                                                        </td>
                                                                                    );
                                                                                })}
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                                </tr>
                                                                {/* Row 2: Total Encaminhado - Acumulado */}
                                                                <tr className="hover:bg-slate-50 transition-colors">
                                                                    <td className="p-3 pl-6 sticky left-0 bg-white hover:bg-slate-50 z-20 border-r border-gray-200 font-bold text-gray-800 w-[320px] min-w-[320px] max-w-[320px]">
                                                                        Total Encaminhado - Acumulado
                                                                    </td>
                                                                    {(() => {
                                                                        let runningEncaminhado = 0;
                                                                        return Object.entries(colunasAgrupadas).map(([ano, colunas]) => {
                                                                            const isExpanded = expandedYears[ano];
                                                                            const canExpand = colunas.length > 1;

                                                                            if (!isExpanded && canExpand) {
                                                                                const sumVal = colunas.reduce((acc, col) => {
                                                                                    const isConsol = metasEditaveis && metasEditaveis.length > 0 && metasEditaveis.some(row => row[col + '_status'] === 'Consolidado');
                                                                                    return acc + (isConsol ? (somaMensal[col] || 0) : 0);
                                                                                }, 0);
                                                                                const legacyVal = (metasEditaveis && metasEditaveis.some(row => row[`ano${ano}_status`] === 'Consolidado')) ? (somaMensal[`ano${ano}`] || 0) : 0;
                                                                                runningEncaminhado += (sumVal > 0 ? sumVal : legacyVal);
                                                                                return (
                                                                                    <td key={`total-${ano}`} className="p-3 text-center font-mono font-bold text-emerald-800 bg-emerald-50/70">
                                                                                        {runningEncaminhado !== 0 ? runningEncaminhado.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 }) : '-'}
                                                                                    </td>
                                                                                );
                                                                            }

                                                                            return (
                                                                                <React.Fragment key={`group-${ano}`}>
                                                                                    {colunas.map((col) => {
                                                                                        const val = somaMensal[col] || 0;
                                                                                        const isConsol = metasEditaveis && metasEditaveis.length > 0 && metasEditaveis.some(row => row[col + '_status'] === 'Consolidado');
                                                                                        if (isConsol) {
                                                                                            runningEncaminhado += val;
                                                                                        }
                                                                                        return (
                                                                                            <td key={col} className="p-3 text-center font-mono font-bold text-gray-700">
                                                                                                {isConsol && runningEncaminhado !== 0 ? runningEncaminhado.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 }) : '-'}
                                                                                            </td>
                                                                                        );
                                                                                    })}
                                                                                </React.Fragment>
                                                                            );
                                                                        });
                                                                    })()}
                                                                </tr>
                                                                {/* Row 3: Total Projeção - Mensal */}
                                                                <tr className="hover:bg-slate-50 transition-colors">
                                                                    <td className="p-3 pl-6 sticky left-0 bg-white hover:bg-slate-50 z-20 border-r border-gray-200 font-bold text-gray-800 w-[320px] min-w-[320px] max-w-[320px]">
                                                                        Total Projeção - Mensal
                                                                    </td>
                                                                    {Object.entries(colunasAgrupadas).map(([ano, colunas]) => {
                                                                        const isExpanded = expandedYears[ano];
                                                                        const canExpand = colunas.length > 1;

                                                                        if (!isExpanded && canExpand) {
                                                                            const sumVal = colunas.reduce((acc, col) => {
                                                                                const isConsol = metasEditaveis && metasEditaveis.length > 0 && metasEditaveis.some(row => row[col + '_status'] === 'Consolidado');
                                                                                return acc + (!isConsol ? (somaMensal[col] || 0) : 0);
                                                                            }, 0);
                                                                            const legacyVal = (!metasEditaveis || !metasEditaveis.some(row => row[`ano${ano}_status`] === 'Consolidado')) ? (somaMensal[`ano${ano}`] || 0) : 0;
                                                                            const finalVal = sumVal > 0 ? sumVal : legacyVal;
                                                                            return (
                                                                                <td key={`total-${ano}`} className="p-3 text-center font-mono font-bold text-gray-700 bg-slate-100/50">
                                                                                    {finalVal !== 0 ? finalVal.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 }) : '-'}
                                                                                </td>
                                                                            );
                                                                        }

                                                                        return (
                                                                            <React.Fragment key={`group-${ano}`}>
                                                                                {colunas.map((col) => {
                                                                                    const val = somaMensal[col] || 0;
                                                                                    const isConsol = metasEditaveis && metasEditaveis.length > 0 && metasEditaveis.some(row => row[col + '_status'] === 'Consolidado');
                                                                                    return (
                                                                                        <td key={col} className="p-3 text-center font-mono font-bold text-gray-700">
                                                                                            {!isConsol && val !== 0 ? val.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 }) : '-'}
                                                                                        </td>
                                                                                    );
                                                                                })}
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                                </tr>
                                                                {/* Row 4: Total Projeção - Acumulado */}
                                                                <tr className="hover:bg-slate-50 transition-colors">
                                                                    <td className="p-3 pl-6 sticky left-0 bg-white hover:bg-slate-50 z-20 border-r border-gray-200 font-bold text-gray-800 w-[320px] min-w-[320px] max-w-[320px]">
                                                                        Total Projeção - Acumulado
                                                                    </td>
                                                                    {(() => {
                                                                        let runningProjecao = 0;
                                                                        return Object.entries(colunasAgrupadas).map(([ano, colunas]) => {
                                                                            const isExpanded = expandedYears[ano];
                                                                            const canExpand = colunas.length > 1;

                                                                            if (!isExpanded && canExpand) {
                                                                                const sumVal = colunas.reduce((acc, col) => {
                                                                                    const isConsol = metasEditaveis && metasEditaveis.length > 0 && metasEditaveis.some(row => row[col + '_status'] === 'Consolidado');
                                                                                    return acc + (!isConsol ? (somaMensal[col] || 0) : 0);
                                                                                }, 0);
                                                                                const legacyVal = (!metasEditaveis || !metasEditaveis.some(row => row[`ano${ano}_status`] === 'Consolidado')) ? (somaMensal[`ano${ano}`] || 0) : 0;
                                                                                runningProjecao += (sumVal > 0 ? sumVal : legacyVal);
                                                                                return (
                                                                                    <td key={`total-${ano}`} className="p-3 text-center font-mono font-bold text-gray-700 bg-slate-100/50">
                                                                                        {runningProjecao !== 0 ? runningProjecao.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 }) : '-'}
                                                                                    </td>
                                                                                );
                                                                            }

                                                                            return (
                                                                                <React.Fragment key={`group-${ano}`}>
                                                                                    {colunas.map((col) => {
                                                                                        const val = somaMensal[col] || 0;
                                                                                        const isConsol = metasEditaveis && metasEditaveis.length > 0 && metasEditaveis.some(row => row[col + '_status'] === 'Consolidado');
                                                                                        if (!isConsol) {
                                                                                            runningProjecao += val;
                                                                                        }
                                                                                        return (
                                                                                            <td key={col} className="p-3 text-center font-mono font-bold text-gray-700">
                                                                                                {!isConsol && runningProjecao !== 0 ? runningProjecao.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 }) : '-'}
                                                                                            </td>
                                                                                        );
                                                                                    })}
                                                                                </React.Fragment>
                                                                            );
                                                                        });
                                                                    })()}
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>

                                                    {/* BOTÃO PARA DETALHAMENTO MENSAL (QUANDO DESLOGADO) */}
                                                    <div className="p-4 border-t border-gray-100 flex justify-start bg-slate-50/50">
                                                        <button
                                                            onClick={() => setShowDetalhesContratos(!showDetalhesContratos)}
                                                            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:border-sabesp-blue text-sabesp-blue rounded-xl text-xs font-bold transition-all shadow-sm cursor-pointer"
                                                        >
                                                            {showDetalhesContratos ? '🙈 Ocultar Detalhe Mensal por Contrato' : '👁️ Visualizar Detalhe Mensal por Contrato'}
                                                        </button>
                                                    </div>

                                                    {/* TABELA DE DETALHES DE CONTRATOS APENAS LEITURA (QUANDO DESLOGADO) */}
                                                    {showDetalhesContratos && (
                                                        <div className="overflow-auto max-h-[600px] scrollbar-thin border-t border-gray-100 animate-fadeIn">
                                                            <table className="w-full text-left border-collapse text-xs relative">
                                                                <thead>
                                                                    <tr className="bg-slate-100 text-slate-700 font-bold uppercase tracking-wider border-b border-gray-200">
                                                                        <th className="p-3 pl-6 w-[140px] min-w-[140px] max-w-[140px] sticky top-0 left-0 bg-slate-100 z-40 border-r border-gray-200">CONTRATO ID</th>
                                                                        <th className="p-3 w-[180px] min-w-[180px] max-w-[180px] sticky top-0 left-[140px] bg-slate-100 z-40 border-r border-gray-200">OBJETO REDUZIDO</th>
                                                                        {Object.entries(colunasAgrupadas).map(([ano, colunas]) => {
                                                                            const isExpanded = expandedYears[ano];
                                                                            const canExpand = colunas.length > 1;

                                                                            if (!isExpanded && canExpand) {
                                                                                return (
                                                                                    <th key={`total-${ano}`} className="p-3 text-center min-w-[120px] font-mono sticky top-0 bg-slate-100 z-30 border-b border-gray-200">
                                                                                        <div className="flex items-center justify-center gap-2">
                                                                                            <span>Total {ano}</span>
                                                                                            <button onClick={() => setExpandedYears(prev => ({...prev, [ano]: true}))} className="text-sabesp-blue font-black text-lg hover:scale-110 transition-transform" title={`Expandir meses de ${ano}`}>
                                                                                                +
                                                                                            </button>
                                                                                        </div>
                                                                                    </th>
                                                                                );
                                                                            }

                                                                            return (
                                                                                <React.Fragment key={`group-${ano}`}>
                                                                                    {colunas.map((mes, index) => (
                                                                                        <th key={mes} className="p-3 text-center min-w-[90px] font-mono sticky top-0 bg-slate-100 z-30 border-b border-gray-200">
                                                                                            <div className="flex items-center justify-center gap-1">
                                                                                                {index === 0 && canExpand && (
                                                                                                    <button onClick={() => setExpandedYears(prev => ({...prev, [ano]: false}))} className="text-gray-400 hover:text-sabesp-blue text-sm font-black mr-1 transition-transform hover:scale-110" title={`Recolher ano ${ano}`}>
                                                                                                        -
                                                                                                    </button>
                                                                                                )}
                                                                                                <span>{formatPeriodHeader(mes)}</span>
                                                                                            </div>
                                                                                        </th>
                                                                                    ))}
                                                                                </React.Fragment>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-gray-100 font-medium text-gray-700">
                                                                    {filteredMetas.length > 0 && (
                                                                        <>
                                                                            {/* Soma Mensal Row */}
                                                                            <tr className="bg-slate-100/90 font-bold border-b border-slate-200 select-none">
                                                                                <td className="p-2 pl-6 sticky left-0 bg-slate-100 z-20 border-r border-gray-200 font-black text-gray-800 w-[140px] min-w-[140px] max-w-[140px]">
                                                                                    SOMA MENSAL
                                                                                </td>
                                                                                <td className="p-2 sticky left-[140px] bg-slate-100 z-20 border-r border-gray-200 font-black text-gray-800 w-[180px] min-w-[180px] max-w-[180px] truncate">
                                                                                    -
                                                                                </td>
                                                                                {Object.entries(colunasAgrupadas).map(([ano, colunas]) => {
                                                                                    const isExpanded = expandedYears[ano];
                                                                                    const canExpand = colunas.length > 1;

                                                                                    if (!isExpanded && canExpand) {
                                                                                        const sumVal = colunas.reduce((acc, c) => acc + (somaMensal[c] || 0), 0);
                                                                                        const legacyVal = somaMensal[`ano${ano}`] || 0;
                                                                                        const finalTotal = sumVal > 0 ? sumVal : legacyVal;
                                                                                        return (
                                                                                            <td key={`total-${ano}`} className="p-2 text-center font-mono font-black text-slate-800 bg-slate-200/80 border-x border-slate-300">
                                                                                                {finalTotal === 0 ? '-' : finalTotal.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 })}
                                                                                            </td>
                                                                                        );
                                                                                    }

                                                                                    return (
                                                                                        <React.Fragment key={`group-${ano}`}>
                                                                                            {colunas.map(mesChave => {
                                                                                                const val = somaMensal[mesChave] || 0;
                                                                                                return (
                                                                                                    <td key={mesChave} className="p-2 text-center font-mono font-black text-slate-800 bg-slate-100/70">
                                                                                                        {val === 0 ? '-' : val.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 })}
                                                                                                    </td>
                                                                                                );
                                                                                            })}
                                                                                        </React.Fragment>
                                                                                    );
                                                                                })}
                                                                            </tr>
                                                                            {/* Total Acumulado Row */}
                                                                            <tr className="bg-slate-100/90 font-bold border-b border-slate-200 select-none">
                                                                                <td className="p-2 pl-6 sticky left-0 bg-slate-100 z-20 border-r border-gray-200 font-black text-[#0C336A] w-[140px] min-w-[140px] max-w-[140px]">
                                                                                    TOTAL ACUMULADO
                                                                                </td>
                                                                                <td className="p-2 sticky left-[140px] bg-slate-100 z-20 border-r border-gray-200 font-black text-[#0C336A] w-[180px] min-w-[180px] max-w-[180px] truncate">
                                                                                    -
                                                                                </td>
                                                                                {(() => {
                                                                                    let acc = 0;
                                                                                    return Object.entries(colunasAgrupadas).map(([ano, colunas]) => {
                                                                                        const isExpanded = expandedYears[ano];
                                                                                        const canExpand = colunas.length > 1;

                                                                                        if (!isExpanded && canExpand) {
                                                                                            const sumVal = colunas.reduce((sum, c) => sum + (somaMensal[c] || 0), 0);
                                                                                            const legacyVal = somaMensal[`ano${ano}`] || 0;
                                                                                            acc += (sumVal > 0 ? sumVal : legacyVal);
                                                                                            return (
                                                                                                <td key={`total-${ano}`} className="p-2 text-center font-mono font-black text-[#0C336A] bg-slate-200/80 border-x border-slate-300">
                                                                                                    {acc === 0 ? '-' : acc.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 })}
                                                                                                </td>
                                                                                            );
                                                                                        }

                                                                                        return (
                                                                                            <React.Fragment key={`group-${ano}`}>
                                                                                                {colunas.map(mesChave => {
                                                                                                    const val = somaMensal[mesChave] || 0;
                                                                                                    acc += val;
                                                                                                    return (
                                                                                                        <td key={mesChave} className="p-2 text-center font-mono font-black text-[#0C336A] bg-slate-100/70">
                                                                                                            {acc === 0 ? '-' : acc.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 })}
                                                                                                        </td>
                                                                                                    );
                                                                                                })}
                                                                                            </React.Fragment>
                                                                                        );
                                                                                    });
                                                                                })()}
                                                                            </tr>
                                                                        </>
                                                                    )}
                                                                    {filteredMetas.length > 0 ? (
                                                                        filteredMetas.map((meta) => (
                                                                            <tr key={meta.id} className="group hover:bg-slate-50/80 transition-colors">
                                                                                <td className="p-2 pl-6 sticky left-0 bg-white group-hover:bg-slate-50 z-20 border-r border-gray-200 w-[140px] min-w-[140px] max-w-[140px]">
                                                                                    <span className="font-mono font-bold text-sabesp-blue px-1">
                                                                                        {meta.contrato_id || '-'}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="p-2 sticky left-[140px] bg-white group-hover:bg-slate-50 z-20 border-r border-gray-200 w-[180px] min-w-[180px] max-w-[180px] truncate">
                                                                                    <span className="text-gray-800 px-1 truncate block max-w-[180px]" title={meta.objeto_reduzido}>
                                                                                        {meta.objeto_reduzido || '-'}
                                                                                    </span>
                                                                                </td>
                                                                                {Object.entries(colunasAgrupadas).map(([ano, colunas]) => {
                                                                                    const isExpanded = expandedYears[ano];
                                                                                    const canExpand = colunas.length > 1;

                                                                                    if (!isExpanded && canExpand) {
                                                                                        const sumVal = colunas.reduce((acc, c) => acc + Number(meta[c] || 0), 0);
                                                                                        const legacyVal = Number(meta[`ano${ano}`] || 0);
                                                                                        const finalTotal = sumVal > 0 ? sumVal : legacyVal;
                                                                                        const displayVal = finalTotal === 0 ? '-' : finalTotal.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 });
                                                                                        return (
                                                                                            <td key={`total-${ano}`} className="px-3 py-2 text-center text-sm font-medium text-slate-700 whitespace-nowrap border-x border-slate-200" style={{ backgroundColor: '#f8fafc' }}>
                                                                                                <span>{displayVal}</span>
                                                                                            </td>
                                                                                        );
                                                                                    }

                                                                                    return (
                                                                                        <React.Fragment key={`group-${ano}`}>
                                                                                            {colunas.map(mesChave => {
                                                                                                const val = Number(meta[mesChave] || 0);
                                                                                                const statusKey = getDbStatusKey(mesChave);
                                                                                                const status = meta[statusKey] || 'Projeção';
                                                                                                const isConsolidado = status === 'Consolidado';
                                                                                                const cellBg = isConsolidado ? '#e6f4ea' : '#f1f3f4';
                                                                                                const cellFg = isConsolidado ? '#137333' : '#3c4043';
                                                                                                const displayVal = val === 0 ? '-' : val.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 5 });
                                                                                                return (
                                                                                                    <td
                                                                                                        key={mesChave}
                                                                                                        className="p-1 text-center select-none font-mono font-bold"
                                                                                                        style={{ backgroundColor: cellBg, color: cellFg }}
                                                                                                    >
                                                                                                        <span>{displayVal}</span>
                                                                                                    </td>
                                                                                                );
                                                                                            })}
                                                                                        </React.Fragment>
                                                                                    );
                                                                                })}
                                                                            </tr>
                                                                        ))
                                                                    ) : (
                                                                        <tr>
                                                                            <td colSpan={2 + colunasMeses.length} className="p-8 text-center text-gray-400 italic">
                                                                                Nenhuma estrutura de planejamento encontrada.
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm">
                                <div className="flex justify-between items-center mb-6"><h3 className="text-lg font-bold text-gray-800 flex items-center gap-2"><span>📐</span> Fluxograma & Vídeo</h3>{isAdmin && (<button onClick={() => setIsFluxoModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-600 rounded-lg text-xs font-bold hover:bg-gray-100 border border-gray-200 transition-all uppercase tracking-wider">📤 Atualizar Arquivos</button>)}</div>
                                <div className="aspect-video w-full max-h-[600px] mb-8">{renderFluxogramaContent()}</div>
                                {isAdmin && data.fluxograma_historico && data.fluxograma_historico.length > 0 && (
                                    <div className="pt-6 border-t border-gray-100 animate-fadeIn">
                                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2"><span>🕒</span> Histórico de Versões (Restrito)</h4>
                                        <div className="space-y-3">{data.fluxograma_historico.map((item, index) => (<div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100"><div className="shrink-0 pt-1"><div className="w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center font-bold text-xs">U</div></div><div className="flex-1"><div className="flex justify-between items-start"><div className="flex flex-col"><p className="text-sm font-bold text-gray-800">{item.usuario || 'Usuário'}</p><p className="text-[10px] text-gray-400 font-mono">{formatData(item.data_registro)}</p></div>{item.arquivo_url && (<button onClick={() => window.open(getFileUrl(item.arquivo_url), '_blank')} className="text-[10px] bg-white border px-3 py-1 rounded-full font-bold">📄 Ver Arquivo</button>)}</div><p className="text-xs text-gray-600 mt-2">{item.descricao}</p></div></div>))}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* --- ABA CONTRATOS --- */}
                    {activeTab === 'Contratos e Execução' && (
                        <div className="py-4 space-y-6 animate-fadeIn">
                            {data.contratos && data.contratos.length > 0 && (
                                <div className="flex justify-end items-center gap-2 mb-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase">Filtrar:</label>
                                    <select
                                        value={filtroContratoId}
                                        onChange={(e) => setFiltroContratoId(e.target.value)}
                                        className="bg-white border border-gray-200 text-gray-700 text-xs font-bold py-2 px-4 rounded-xl outline-none shadow-sm"
                                    >
                                        <option value="Todos">Todos os Contratos</option>
                                        {data.contratos.map(c => (
                                            <option key={c.id} value={c.id}>{c.numero_contrato || c.codigo_contrato} - {c.nome}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {(filtroContratoId === 'Todos' ? data.contratos : data.contratos.filter(c => c.id === parseInt(filtroContratoId))).map((contrato) => {
                                const isExpanded = expandedContrato === contrato.id;
                                const marcosOrdenados = contrato.marcos ? [...contrato.marcos].sort((a, b) => getTimestamp(a.data_marco) - getTimestamp(b.data_marco)) : [];
                                const descricaoContrato = contrato.objetoResumidoETE || contrato.descricao;

                                return (
                                    <div key={contrato.id} className={`bg-white rounded-3xl border transition-all duration-300 overflow-hidden ${isExpanded ? 'border-sabesp-blue/30 shadow-md' : 'border-gray-200 shadow-sm hover:shadow-md'}`}>
                                        <div
                                            onClick={() => setExpandedContrato(isExpanded ? null : contrato.id)}
                                            className="cursor-pointer bg-slate-50/50 hover:bg-slate-50 px-4 md:px-6 py-5 flex flex-col md:flex-row md:items-center justify-between transition-colors gap-4"
                                        >
                                            <div className="flex items-center gap-4 flex-1">
                                                <div className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center font-bold text-lg transition-transform duration-300 ${isExpanded ? 'bg-sabesp-blue text-white rotate-90' : 'bg-white border border-gray-200 text-gray-400'}`}>
                                                    ›
                                                </div>

                                                <div className="flex-1">
                                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                                        <span className="text-[10px] md:text-xs font-mono font-bold text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded shadow-sm">
                                                            {contrato.codigo_contrato || "S/N"}
                                                        </span>
                                                        <span className={`text-[9px] md:text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded text-white`} style={{ backgroundColor: contrato.status_cor || '#999' }}>
                                                            {contrato.status_nome || 'Sem Status'}
                                                        </span>
                                                    </div>
                                                    <h3 className="font-bold text-gray-800 text-sm md:text-lg leading-tight">{contrato.nome}</h3>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between md:justify-end gap-4 md:gap-8 border-t md:border-t-0 pt-4 md:pt-0">
                                                <div className="text-left md:text-right">
                                                    <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-wider">Investimento</p>
                                                    <p className="font-bold text-sabesp-blue text-xs md:text-sm">{formatMoney(contrato.valorContratoCadastroETE)}</p>
                                                </div>

                                                {isAdmin && (
                                                    <div className="flex gap-2 shrink-0">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleEditarContrato(contrato); }}
                                                            className="w-9 h-9 md:w-8 md:h-8 flex items-center justify-center bg-white md:bg-gray-100 hover:bg-yellow-100 hover:text-yellow-700 text-gray-500 rounded-lg transition-colors border border-gray-200 shadow-sm md:shadow-none"
                                                            title="Editar Escopo e Valor"
                                                        >
                                                            ✏️
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); openMarcoModal(contrato.id); }}
                                                            className="px-3 py-2 md:py-1.5 bg-white border border-gray-200 hover:border-sabesp-blue text-sabesp-blue rounded-lg text-[10px] md:text-xs font-bold transition-all shadow-sm flex items-center gap-1"
                                                        >
                                                            <span>+</span> Marco
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="p-4 md:p-8 border-t border-gray-100 bg-white animate-fadeIn">
                                                {descricaoContrato && descricaoContrato.trim() !== "" && (
                                                    <div className="mb-8 bg-gray-50 p-4 md:p-6 rounded-2xl border border-gray-100">
                                                        <h4 className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Escopo</h4>
                                                        <p className="text-xs md:text-sm text-gray-700 leading-relaxed text-justify">{descricaoContrato}</p>
                                                    </div>
                                                )}

                                                <h4 className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-widest mb-6 flex items-center gap-2">
                                                    <span>🚀</span> LINHA DO TEMPO
                                                </h4>

                                                {marcosOrdenados.length > 0 ? (
                                                    <div className="relative overflow-x-auto pt-4 pb-10 scrollbar-thin px-2">
                                                        <div className="flex gap-0 min-w-max items-start pt-2">
                                                            {marcosOrdenados.map((marco, idx) => {
                                                                const isCompleted = marco.status === 'Concluído';
                                                                const isInProgress = marco.status === 'Em andamento';
                                                                const isLast = idx === marcosOrdenados.length - 1;

                                                                let circleColor = "bg-white border-gray-200 text-gray-400";
                                                                let textColor = "text-gray-800";
                                                                let dateColor = "text-gray-400";
                                                                let barColor = "bg-gray-200";

                                                                if (isCompleted) {
                                                                    circleColor = "bg-emerald-500 border-emerald-200 text-white";
                                                                    barColor = "bg-emerald-400";
                                                                } else if (isInProgress) {
                                                                    circleColor = "bg-blue-500 border-blue-200 text-white animate-pulse";
                                                                    textColor = "text-blue-700 font-bold";
                                                                    dateColor = "text-blue-500";
                                                                    barColor = "bg-blue-200";
                                                                }

                                                                return (
                                                                    <div
                                                                        key={idx}
                                                                        className="relative flex flex-col items-center w-32 md:w-40 group"
                                                                    >
                                                                        {!isLast && (<div className={`absolute top-[18px] left-[50%] w-full h-[3px] -z-0 ${barColor}`}></div>)}

                                                                        <div
                                                                            className={`w-8 h-8 md:w-10 md:h-10 rounded-full border-4 shadow-sm z-10 flex items-center justify-center transition-all duration-300 cursor-pointer ${circleColor}`}
                                                                            onMouseEnter={(e) => handleMarcoMouseEnter(e, marco)}
                                                                            onMouseLeave={handleMarcoMouseLeave}
                                                                        >
                                                                            {isCompleted ? <span className="text-sm md:text-lg font-bold">✓</span> : <span className="font-bold text-xs">{idx + 1}</span>}
                                                                        </div>
                                                                        <div className="mt-3 text-center px-1 w-full">
                                                                            <span className={`block text-[9px] md:text-[10px] font-bold uppercase mb-1 ${dateColor}`}>{marco.data_marco}</span>
                                                                            <p className={`text-[10px] md:text-xs leading-tight mb-2 h-12 flex items-center justify-center ${textColor}`}>{marco.titulo}</p>
                                                                            {isAdmin && (
                                                                                <button onClick={() => handleEditarMarco(marco, contrato.id)} className="mt-1 text-[9px] md:text-[10px] text-sabesp-blue font-bold hover:underline opacity-60 hover:opacity-100">Editar</button>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                                        <p className="text-gray-400 text-xs">Nenhum marco cadastrado.</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <footer className="footer-container text-center py-8 text-gray-400 text-xs mt-auto"><p>© 2026 Sabesp - Superintendência de Gestão de Empreendimentos</p></footer>

            <EdicaoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} empreendimento={data} onUpdateSuccess={() => { fetchDetalhes(); setIsModalOpen(false); }} />
            <AdicionarMarcoModal isOpen={isMarcoModalOpen} onClose={handleCloseMarcoModal} empreendimentoId={data.id} contratoId={selectedContratoId} marcoParaEditar={marcoEmEdicao} onSuccess={() => { fetchDetalhes(); handleCloseMarcoModal(); }} />
            <FluxogramaModal isOpen={isFluxoModalOpen} onClose={() => setIsFluxoModalOpen(false)} empreendimentoId={data.id} currentData={data} onSuccess={() => { fetchDetalhes(); setIsFluxoModalOpen(false); }} />
            <EditarContratoModal isOpen={isContratoModalOpen} onClose={() => setIsContratoModalOpen(false)} contrato={contratoEmEdicao} onSuccess={fetchDetalhes} />

            <ExcluirModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmarExclusaoReal}
                titulo={itemToDelete?.tipo === 'contrato_planejamento' ? "Remover Contrato" : "Remover Arquivo"}
                mensagem={
                    itemToDelete?.tipo === 'contrato_planejamento'
                        ? `Tem certeza que deseja remover este planejamento do contrato?\nEssa ação removerá todas as metas associadas a ele de forma permanente e ficará registrada no histórico.`
                        : `Tem certeza que deseja remover este ${itemToDelete?.label || 'arquivo'}?\nEssa ação não poderá ser desfeita e ficará registrada no histórico.`
                }
                item={itemToDelete?.label}
            />

            {/* 📝 POPUP DE EDIÇÃO RÁPIDA DE CÉLULA INDEPENDENTE */}
            {isCellModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center animate-fadeIn">
                    <div className="bg-white rounded-[2rem] p-8 shadow-2xl border border-gray-100 max-w-sm w-full mx-4 transform transition-all">
                        <h3 className="text-lg font-black text-gray-800 mb-6 flex items-center gap-3">
                            <span className="w-10 h-10 rounded-full bg-blue-50 text-sabesp-blue flex items-center justify-center text-xl">✏️</span>
                            Editar {cellEmEdicao ? formatPeriodHeader(cellEmEdicao.coluna) : ''}
                        </h3>

                        <div className="space-y-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Valor Proposto</label>
                                <input
                                    type="number"
                                    step="any"
                                    value={edicaoValor}
                                    placeholder="Ex: 7.102 ou 18.146"
                                    onChange={(e) => setEdicaoValor(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCell(); }}
                                    className="w-full bg-slate-50 border-2 border-transparent focus:border-sabesp-blue focus:bg-white rounded-2xl px-5 py-4 text-lg font-black text-gray-800 outline-none transition-all shadow-inner"
                                    autoFocus
                                />
                                <p className="text-[10px] text-gray-400 mt-2 font-medium">Você pode usar casas decimais. Pressione <kbd className="bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">Enter</kbd> para salvar.</p>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Status da Meta</label>
                                <select
                                    value={edicaoStatus}
                                    onChange={(e) => setEdicaoStatus(e.target.value)}
                                    className="w-full bg-slate-50 border-2 border-transparent focus:border-sabesp-blue focus:bg-white rounded-2xl px-5 py-4 text-sm font-bold text-gray-800 outline-none transition-all cursor-pointer shadow-inner appearance-none"
                                >
                                    <option value="Consolidado">🟢 Realizado (Consolidado)</option>
                                    <option value="Projeção">🔵 Planejado (Projeção)</option>
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-8">
                            <button
                                onClick={() => setIsCellModalOpen(false)}
                                className="flex-1 py-3.5 bg-slate-100 hover:bg-slate-200 text-gray-600 rounded-2xl text-sm font-bold transition-all"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveCell}
                                className="flex-1 py-3.5 bg-gradient-to-r from-sabesp-blue to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-2xl text-sm font-bold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                            >
                                Salvar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 📅 MODAL DE EDIÇÃO DO MÊS INTEIRO EM MASSA */}
            {isMonthModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center animate-fadeIn">
                    <div className="bg-white rounded-[2rem] p-8 shadow-2xl border border-gray-100 max-w-xl w-full mx-4 transform transition-all flex flex-col max-h-[85vh]">
                        <h3 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-3">
                            <span className="w-10 h-10 rounded-full bg-blue-50 text-sabesp-blue flex items-center justify-center text-xl">📅</span>
                            Editar todos para {selectedMonthCol ? formatPeriodHeader(selectedMonthCol) : ''}
                        </h3>

                        <div className="flex-1 overflow-y-auto pr-2 space-y-4 my-4 scrollbar-thin">
                            {metasEditaveis.map(meta => {
                                const current = monthValues[meta.id] || { val: '', status: 'Projeção' };
                                return (
                                    <div key={meta.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100/60 flex flex-col md:flex-row md:items-center justify-between gap-3">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-mono font-bold text-sabesp-blue">{meta.contrato_id || "Sem Contrato ID"}</p>
                                            <p className="text-xs font-medium text-gray-500 truncate" title={meta.objeto_reduzido}>{meta.objeto_reduzido || "Sem Objeto"}</p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <input
                                                type="number"
                                                step="any"
                                                value={current.val}
                                                placeholder="Valor"
                                                onChange={(e) => setMonthValues(prev => ({
                                                    ...prev,
                                                    [meta.id]: { ...prev[meta.id], val: e.target.value }
                                                }))}
                                                className="w-24 bg-white border border-gray-200 focus:border-sabesp-blue rounded-xl px-3 py-2 text-xs font-mono font-bold text-gray-800 outline-none transition-all shadow-sm"
                                            />
                                            <select
                                                value={current.status}
                                                onChange={(e) => setMonthValues(prev => ({
                                                    ...prev,
                                                    [meta.id]: { ...prev[meta.id], status: e.target.value }
                                                }))}
                                                className="bg-white border border-gray-200 focus:border-sabesp-blue rounded-xl px-2 py-2 text-xs font-bold text-gray-800 outline-none transition-all cursor-pointer shadow-sm"
                                            >
                                                <option value="Consolidado">🟢 Realizado</option>
                                                <option value="Projeção">🔵 Planejado</option>
                                            </select>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
                            <button
                                onClick={() => setIsMonthModalOpen(false)}
                                disabled={salvandoModal}
                                className="flex-1 py-3.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-gray-600 rounded-2xl text-sm font-bold transition-all"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveMonth}
                                disabled={salvandoModal}
                                className="flex-1 py-3.5 bg-gradient-to-r from-sabesp-blue to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 text-white rounded-2xl text-sm font-bold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                            >
                                {salvandoModal ? "Salvando..." : "Salvar Todos"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ✏️ MODAL DE EDIÇÃO DE LINHA (CONTRATO) INTEIRA */}
            {isRowModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center animate-fadeIn">
                    <div className="bg-white rounded-[2rem] p-8 shadow-2xl border border-gray-100 max-w-3xl w-full mx-4 transform transition-all flex flex-col max-h-[85vh]">
                        <h3 className="text-lg font-black text-gray-800 mb-2 flex items-center gap-3">
                            <span className="w-10 h-10 rounded-full bg-amber-50 text-amber-500 flex items-center justify-center text-xl">✏️</span>
                            Editar Planejamento Completo
                        </h3>
                        <div className="mb-4">
                            <p className="text-xs font-mono font-bold text-sabesp-blue">Contrato: {selectedMetaRow?.contrato_id || "-"}</p>
                            <p className="text-xs text-gray-500 truncate font-semibold" title={selectedMetaRow?.objeto_reduzido}>Objeto: {selectedMetaRow?.objeto_reduzido || "-"}</p>
                        </div>

                        <div className="flex-1 overflow-y-auto pr-2 my-2 scrollbar-thin">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {colunasMeses.map(col => {
                                    const current = rowValues[col] || { val: '', status: 'Projeção' };
                                    return (
                                        <div key={col} className="p-3 bg-slate-50 border border-slate-100 rounded-xl flex flex-col justify-between gap-2">
                                            <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider font-mono">
                                                {formatPeriodHeader(col)}
                                            </p>
                                            <div className="space-y-1.5">
                                                <input
                                                    type="number"
                                                    step="any"
                                                    value={current.val}
                                                    placeholder="Valor"
                                                    onChange={(e) => setRowValues(prev => ({
                                                        ...prev,
                                                        [col]: { ...prev[col], val: e.target.value }
                                                    }))}
                                                    className="w-full bg-white border border-gray-200 focus:border-sabesp-blue rounded-lg px-2.5 py-1.5 text-xs font-mono font-bold text-gray-800 outline-none transition-all"
                                                />
                                                <select
                                                    value={current.status}
                                                    onChange={(e) => setRowValues(prev => ({
                                                        ...prev,
                                                        [col]: { ...prev[col], status: e.target.value }
                                                    }))}
                                                    className="w-full bg-white border border-gray-200 focus:border-sabesp-blue rounded-lg px-2 py-1.5 text-xs font-bold text-gray-800 outline-none transition-all cursor-pointer"
                                                >
                                                    <option value="Consolidado">🟢 Realizado</option>
                                                    <option value="Projeção">🔵 Planejado</option>
                                                </select>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
                            <button
                                onClick={() => setIsRowModalOpen(false)}
                                disabled={salvandoModal}
                                className="flex-1 py-3.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-gray-600 rounded-2xl text-sm font-bold transition-all"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveRow}
                                disabled={salvandoModal}
                                className="flex-1 py-3.5 bg-gradient-to-r from-sabesp-blue to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 text-white rounded-2xl text-sm font-bold transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                            >
                                {salvandoModal ? "Salvando..." : "Salvar Linha"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 🗑️ MODAL DE CONFIRMAÇÃO DE EXCLUSÃO DE ANO COMPLETO */}
            {isDeleteYearModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fadeIn">
                    <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-gray-100 space-y-4">
                        <div className="flex items-center gap-3 text-red-600 border-b border-gray-100 pb-3">
                            <div className="p-2.5 bg-red-50 rounded-2xl text-xl">⚠️</div>
                            <div>
                                <h3 className="font-extrabold text-base text-gray-900">Excluir Ano do Planejamento</h3>
                                <p className="text-xs text-gray-400 font-medium">Esta ação não poderá ser desfeita</p>
                            </div>
                        </div>

                        <p className="text-xs text-gray-600 leading-relaxed">
                            Tem certeza que deseja excluir permanentemente o ano <strong className="text-red-600 font-bold">{yearToDelete}</strong> da tabela de projeção de economias?
                            <br/><br/>
                            Todos os dados mensais e valores registrados para <strong className="text-slate-800 font-bold">{yearToDelete}</strong> em todos os contratos serão removidos e detalhados nos <strong className="text-sabesp-blue font-bold">Logs de Auditoria</strong>.
                        </p>

                        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                            <button
                                onClick={() => { setIsDeleteYearModalOpen(false); setYearToDelete(null); }}
                                disabled={excluindoAno}
                                className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleConfirmDeleteYear}
                                disabled={excluindoAno}
                                className="px-4 py-2 text-xs font-bold bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer"
                            >
                                {excluindoAno ? 'Excluindo...' : '🗑️ Confirmar Exclusão'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Tooltip Global Flutuante para os Marcos */}
            {hoveredMarco && (
                <>
                    <style>{`
                        @keyframes fadeInSimple {
                            from { opacity: 0; }
                            to { opacity: 1; }
                        }
                    `}</style>
                    <div
                        className="absolute z-[9999] transform -translate-x-1/2 -translate-y-full pointer-events-none text-center"
                        style={{ 
                            left: `${hoveredMarco.x}px`, 
                            top: `${hoveredMarco.y - 12}px`,
                            animation: 'fadeInSimple 0.15s ease-out forwards'
                        }}
                    >
                        <div className="bg-slate-900/95 text-white text-xs rounded-xl p-3 shadow-xl backdrop-blur-sm border border-slate-700 max-w-xs w-56">
                            <p className="font-semibold text-[10px] uppercase text-sabesp-cyan tracking-wider mb-1">{hoveredMarco.titulo}</p>
                            <p className="text-gray-300 font-light leading-relaxed break-words whitespace-pre-line">{hoveredMarco.descricao}</p>
                        </div>
                        <div className="w-2 h-2 bg-slate-900 rotate-45 -mt-1 mx-auto border-r border-b border-slate-700/50"></div>
                    </div>
                </>
            )}
        </div>
    </>);
}

export default DetalheEmpreendimento;
